import { getVaultTranslationOpacity, getVaultPersonalTranslationOpacity } from "../../shared/vaultTranslations";
import { updateVaultBreakersDom } from "./vaultBreakersDom";
import { setCapyShieldHue } from "./capyShield";
import { appendCodexDebugLog } from "./codexDebugLog";
import {
    clampVaultBreakerCrackDurationSeconds,
    getVaultBreakerCrackDurationSeconds,
    VAULT_BREAKER_GROWING_STASH_BONUS_POINTS,
    VAULT_BREAKER_GROWING_STASH_REVEAL_MS,
    VAULT_BREAKER_LAST_QUESTION_MIN_VALUE,
    VAULT_BREAKER_MASSIVE_LOOT_REVEAL_MS,
    VAULT_BREAKER_MAX_CRACK_DURATION_SECONDS,
    VAULT_BREAKER_MIN_CRACK_DURATION_SECONDS
} from "../../shared/game";
import type {
    RoomView,
    VaultBreakerAwardView,
    VaultBreakerChoiceView,
    VaultBreakerCrackAdvanceIdentity,
    VaultBreakerCrackNumber,
    VaultBreakerCrackStatus,
    VaultBreakerRoomView,
    VaultBreakerTeamView,
    VaultBreakerTileView
} from "../../shared/game";
import { renderActiveHostJoinUtility } from "./activeHostUtility";
import { getLanguage, localizeRuntimeText, t } from "./i18n";
import {
    getPlayerRosterDisplayName,
    getPlayerRosterListClass,
    getPlayerRosterPlacementClass,
    getPlayerRenamePendingClass,
    positionOpenPlayerRosterActionsMenus,
    renderPlayerRosterActionsMenu,
    syncPlayerRosterLayout
} from "./playerRoster";
import { downloadPlayerHistory, renderPlayerHistoryHeaderMenu } from "./playerHistoryDownload";
import { SITE_TUNING } from "./siteTuning";
import { VaultCapyRunner } from "./vaultCapyRunner";
import { VaultAnswerCover } from "./vaultAnswerCover";
import { VaultCategoryZoom } from "./vaultCategoryZoom";
import { createVaultCapyShieldElement } from "./vaultCapyShield";
import { VaultBreakersAudio, type VaultBreakersQuestionTheme } from "./vaultBreakersAudio";
import { renderZiarcadiaIcon } from "./ziarcadiaIcons";

type VaultBreakersViewOptions = {
    onChooseVault: (vaultId: string) => void;
    onSelectChoice: (choiceId: string) => void;
    onNextCrack: (identity: VaultBreakerCrackAdvanceIdentity) => void;
    onFinishRound: () => void;
    onRollChooser: () => void;
    onToggleFinalVault?: () => void;
    onUnlockNextValue?: () => void;
    onAdjustCrackDuration?: (durationSeconds: number) => void;
    onPromptRenameParticipant: (sessionId: string) => void;
    onKickParticipant: (sessionId: string) => void;
    onAdjustScore: (sessionId: string, delta: number) => void;
    onAfterRender?: () => void;
};

export type VaultBreakersView = {
    setRoom: (room: RoomView) => void;
    destroy: () => void;
};

const getVaultBreakerCrackAdvanceIdentity = (room: RoomView | null): VaultBreakerCrackAdvanceIdentity | null => {
    const vault = room?.vaultBreakers ?? null;

    if (!room?.viewer.isHost || !vault?.currentVaultId || vault.crackNumber === null) {
        return null;
    }

    return {
        roundNumber: vault.roundNumber,
        vaultId: vault.currentVaultId,
        crackNumber: vault.crackNumber,
        crackStartedAt: vault.crackStartedAt
    };
};

const escapeHtml = (value: string): string =>
    value.replace(/[&<>"']/g, (character) => {
        switch (character) {
            case "&":
                return "&amp;";
            case "<":
                return "&lt;";
            case ">":
                return "&gt;";
            case '"':
                return "&quot;";
            case "'":
                return "&#39;";
            default:
                return character;
        }
    });

const VAULT_BREAKER_DISPLAY_WORD_PATTERN = /[A-Za-z]+(?:'[A-Za-z]+)?/g;
const VAULT_BREAKER_ALWAYS_UPPERCASE_TERMS = new Set([
    "ai",
    "ar",
    "dna",
    "fbi",
    "gps",
    "id",
    "ktx",
    "nasa",
    "nba",
    "pc",
    "pe",
    "tv",
    "uk",
    "url",
    "usa",
    "usb",
    "vr"
]);
const VAULT_BREAKER_TYPED_UPPERCASE_TERMS = new Set(["US"]);

const formatVaultBreakerDisplayWord = (word: string): string => {
    const upperWord = word.toUpperCase();
    const lowerWord = word.toLowerCase();

    if (VAULT_BREAKER_ALWAYS_UPPERCASE_TERMS.has(lowerWord) || (word === upperWord && VAULT_BREAKER_TYPED_UPPERCASE_TERMS.has(upperWord))) {
        return upperWord;
    }

    return `${lowerWord.charAt(0).toUpperCase()}${lowerWord.slice(1)}`;
};

const formatVaultBreakerDisplayTerm = (value: string | null | undefined): string => {
    const normalized = (value ?? "").trim().replace(/\s+/g, " ");

    if (!/[A-Za-z]/.test(normalized)) {
        return normalized;
    }

    return normalized.replace(VAULT_BREAKER_DISPLAY_WORD_PATTERN, formatVaultBreakerDisplayWord);
};

const getVaultBreakerSupplementalInfo = (value: string | null | undefined): string => (value ?? "").trim();

const hasVaultBreakerSupplementalInfo = (choices: readonly Pick<VaultBreakerChoiceView, "korean">[]): boolean =>
    choices.some((choice) => getVaultBreakerSupplementalInfo(choice.korean).length > 0);

const getVaultPromptFitClass = (prompt: string): string => {
    const normalized = prompt.trim().replace(/\s+/g, " ");
    const wordCount = normalized ? normalized.split(" ").length : 0;

    if (normalized.length >= 96 || wordCount >= 16) {
        return "is-extra-long-prompt";
    }

    if (normalized.length >= 58 || wordCount >= 10) {
        return "is-long-prompt";
    }

    return "";
};

const getVaultPlayerNameFitClass = (name: string): string => {
    const characterCount = Array.from(name.trim()).length;

    if (characterCount >= 24) {
        return "is-extra-long-name";
    }

    if (characterCount >= 18) {
        return "is-long-name";
    }

    return "";
};

const formatMoney = (points: number): string => `$${Math.max(0, Math.trunc(points)).toLocaleString("en-US")}`;

const formatMoneyDelta = (points: number): string => `+${formatMoney(points)}`;

type VaultAwardBreakdownItem = {
    label: string;
    points: number;
    tone: "vault" | "quick" | "effort" | "loot" | "diamond" | "stash";
    valueLabel?: string;
    detailLabel?: string;
    subLabel?: string;
    subPoints?: number;
};

const normalizeAwardPoints = (points: number | undefined): number =>
    typeof points === "number" && Number.isFinite(points) ? Math.max(0, Math.trunc(points)) : 0;

const normalizePointMultiplier = (pointMultiplier: number | undefined): number =>
    typeof pointMultiplier === "number" && Number.isFinite(pointMultiplier) ? Math.max(1, Math.trunc(pointMultiplier)) : 1;

const normalizeDiamondBonusPoints = (diamondBonusPoints: number | undefined): number =>
    typeof diamondBonusPoints === "number" && Number.isFinite(diamondBonusPoints)
        ? Math.max(0, Math.trunc(diamondBonusPoints))
        : 0;

const normalizeGrowingStashBonusPoints = (growingStashBonusPoints: number | undefined): number =>
    typeof growingStashBonusPoints === "number" && Number.isFinite(growingStashBonusPoints)
        ? Math.max(0, Math.trunc(growingStashBonusPoints))
        : 0;

const getBoardGrowingStashBonusPoints = (vault: VaultBreakerRoomView): number =>
    vault.tiles.reduce(
        (highest, tile) => Math.max(highest, normalizeGrowingStashBonusPoints(tile.growingStashBonusPoints)),
        normalizeGrowingStashBonusPoints(vault.growingStashBonusPoints)
    );

const VAULT_BONUS_ICON_STACK_MAX = 3;

const getVaultBonusStackCount = (bonusPoints: number, stepPoints: number): number =>
    bonusPoints > 0 ? Math.max(1, Math.min(VAULT_BONUS_ICON_STACK_MAX, Math.ceil(bonusPoints / Math.max(1, stepPoints)))) : 0;

const getVaultGrowingStashRevealDeltaPoints = (growingStashBonusPoints: number | undefined): number => {
    const normalizedGrowingStashBonusPoints = normalizeGrowingStashBonusPoints(growingStashBonusPoints);

    return normalizedGrowingStashBonusPoints > 0
        ? Math.min(VAULT_BREAKER_GROWING_STASH_BONUS_POINTS, normalizedGrowingStashBonusPoints)
        : VAULT_BREAKER_GROWING_STASH_BONUS_POINTS;
};

type VaultValueDisplayTile = Pick<
    VaultBreakerTileView,
    "value" | "pointMultiplier" | "diamondBonusPoints" | "growingStashBonusPoints"
>;

const getVaultBaseDisplayValue = (tile: VaultValueDisplayTile): number =>
    Math.max(0, Math.trunc(tile.value)) +
    normalizeGrowingStashBonusPoints(tile.growingStashBonusPoints) +
    normalizeDiamondBonusPoints(tile.diamondBonusPoints);

const getVaultDisplayValue = (tile: VaultValueDisplayTile): number =>
    getVaultBaseDisplayValue(tile) * normalizePointMultiplier(tile.pointMultiplier);

const formatVaultDisplayValue = (tile: VaultValueDisplayTile): string => {
    const value = formatMoney(getVaultDisplayValue(tile));
    const pointMultiplier = normalizePointMultiplier(tile.pointMultiplier);
    const growingStashBonusPoints = normalizeGrowingStashBonusPoints(tile.growingStashBonusPoints);
    const diamondBonusPoints = normalizeDiamondBonusPoints(tile.diamondBonusPoints);

    return [
        value,
        growingStashBonusPoints > 0 ? `${t("vaultGrowingStashLabel")} +${formatMoney(growingStashBonusPoints)}` : "",
        diamondBonusPoints > 0 ? `${t("vaultDiamondLabel")} +${formatMoney(diamondBonusPoints)}` : "",
        pointMultiplier > 1 ? `${t("vaultAwardMassiveLootLabel")} x${pointMultiplier}` : ""
    ]
        .filter(Boolean)
        .join(" ");
};

const renderVaultGrowingStashBills = (count = 1): string => {
    const stackCount = Math.max(1, Math.min(VAULT_BONUS_ICON_STACK_MAX, Math.trunc(count)));
    const bills = Array.from(
        { length: stackCount },
        () => `<span class="vault-growing-stash-bills"><span></span><span></span><span></span></span>`
    ).join("");

    return `<span class="vault-growing-stash-icons" aria-hidden="true">${bills}</span>`;
};

const renderVaultDiamondGem = (): string => `<span class="vault-diamond-gem" aria-hidden="true"></span>`;

const renderVaultDiamondGems = (count = 1): string => {
    const stackCount = Math.max(1, Math.min(VAULT_BONUS_ICON_STACK_MAX, Math.trunc(count)));
    const gems = Array.from({ length: stackCount }, () => renderVaultDiamondGem()).join("");

    return `<span class="vault-diamond-icons" aria-hidden="true">${gems}</span>`;
};

const renderVaultDisplayMoney = (tile: VaultValueDisplayTile, banknote = false): string => {
    const classes = [
        "vault-display-money",

        normalizeGrowingStashBonusPoints(tile.growingStashBonusPoints) > 0 ? "is-stash" : "",
        normalizeDiamondBonusPoints(tile.diamondBonusPoints) > 0 ? "is-diamond" : "",
        normalizePointMultiplier(tile.pointMultiplier) > 1 ? "is-massive-loot" : ""
    ]
        .filter(Boolean)
        .join(" ");

    const money = formatMoney(getVaultDisplayValue(tile));
    if (banknote) {
        const amount = money.slice(1);
        // Fit the printed denomination, accounting for narrower comma glyphs.
        const glyphWidth = [...amount].reduce((width, glyph) => width + (glyph === "," ? 0.28 : 0.58), 0.24);
        const numberSize = Math.min(48, 94 / glyphWidth).toFixed(2);
        return `<span class="${classes}" style="--vault-banknote-number-size:${numberSize}cqi"><span class="vault-banknote-currency">$</span><span class="vault-banknote-amount">${escapeHtml(amount)}</span></span>`;
    }
    return `<span class="${classes}">${escapeHtml(money)}</span>`;
};

const renderVaultDiamondWorthChip = (tile: VaultValueDisplayTile, compact = false): string => {
    const diamondBonusPoints = normalizeDiamondBonusPoints(tile.diamondBonusPoints);

    return diamondBonusPoints > 0
        ? `<span class="vault-diamond-worth${compact ? " is-compact" : ""}">${renderVaultDiamondGems(
              getVaultBonusStackCount(diamondBonusPoints, 200)
          )}<span>${escapeHtml(`+${formatMoney(diamondBonusPoints)}`)}</span></span>`
        : "";
};

const renderVaultGrowingStashWorthChip = (tile: VaultValueDisplayTile, compact = false): string => {
    const growingStashBonusPoints = normalizeGrowingStashBonusPoints(tile.growingStashBonusPoints);

    return growingStashBonusPoints > 0
        ? `<span class="vault-growing-stash-worth${compact ? " is-compact" : ""}">${renderVaultGrowingStashBills(
              getVaultBonusStackCount(growingStashBonusPoints, 100)
          )}<span>${escapeHtml(`+${formatMoney(growingStashBonusPoints)}`)}</span></span>`
        : "";
};

const renderVaultDisplayValue = (
    tile: VaultValueDisplayTile,
    options: { showMassiveLootLabel?: boolean; compact?: boolean; showBonusChips?: boolean; banknote?: boolean } = {}
): string => {
    const value = renderVaultDisplayMoney(tile, options.banknote);
    const pointMultiplier = normalizePointMultiplier(tile.pointMultiplier);
    const showBonusChips = options.showBonusChips !== false;
    const stashChip = showBonusChips ? renderVaultGrowingStashWorthChip(tile, options.compact === true) : "";
    const diamondChip = showBonusChips ? renderVaultDiamondWorthChip(tile, options.compact === true) : "";
    const modifierChips = `${stashChip}${diamondChip}`;
    const modifierClasses = [
        "vault-value-modifiers",
        stashChip ? "has-stash" : "",
        diamondChip ? "has-diamond" : "",
        options.compact === true ? "is-compact" : "",
        pointMultiplier > 1 ? "has-massive-loot" : ""
    ]
        .filter(Boolean)
        .join(" ");

    if (options.compact) {
        return modifierChips ? `${value}<span class="${modifierClasses}">${modifierChips}</span>` : value;
    }

    if (pointMultiplier <= 1) {
        return modifierChips ? `${value}<span class="${modifierClasses}">${modifierChips}</span>` : value;
    }

    const multiplier = escapeHtml(String(pointMultiplier));
    const multiplierLabel = t("vaultAwardMassiveLootLabel");
    const multiplierClass = options.showMassiveLootLabel ? " is-massive-loot" : "";
    const multiplierContent = options.showMassiveLootLabel
        ? `<span class="vault-value-multiplier-main">x${multiplier}!</span><span class="vault-value-multiplier-label">${escapeHtml(
              multiplierLabel
          )}</span>`
        : `x${multiplier}!`;
    const multiplierMarkup = `<span class="vault-value-multiplier${multiplierClass}">${multiplierContent}</span>`;
    const stackedModifiers = `<span class="${modifierClasses}">${modifierChips}${multiplierMarkup}</span>`;

    return `${value}${stackedModifiers}`;
};

const renderVaultMassiveLootBadge = (pointMultiplier: number | undefined): string => {
    const normalizedPointMultiplier = normalizePointMultiplier(pointMultiplier);

    return normalizedPointMultiplier > 1
        ? `<span class="vault-massive-loot-badge">${escapeHtml(t("vaultMassiveLootLabel"))} x${escapeHtml(
              String(normalizedPointMultiplier)
          )}!</span>`
        : "";
};

const isMassiveLootRevealActive = (vault: VaultBreakerRoomView, now: number): boolean =>
    normalizePointMultiplier(vault.pointMultiplier) > 1 &&
    vault.massiveLootRevealEndsAt !== null &&
    now < vault.massiveLootRevealEndsAt &&
    !vault.roundComplete;

const isGrowingStashRevealActive = (vault: VaultBreakerRoomView, now: number): boolean =>
    normalizeGrowingStashBonusPoints(vault.growingStashBonusPoints) > 0 &&
    vault.currentVaultId !== null &&
    vault.currentVaultId === vault.growingStashVaultId &&
    vault.growingStashRevealEndsAt !== null &&
    now < vault.growingStashRevealEndsAt &&
    !vault.roundComplete;

const renderVaultMassiveLootReveal = (pointMultiplier: number | undefined): string => {
    const normalizedPointMultiplier = normalizePointMultiplier(pointMultiplier);
    const particles = Array.from({ length: 10 }, (_, index) => {
        const x = 10 + ((index * 23) % 82);
        const y = 16 + ((index * 31) % 70);
        const size = 4 + (index % 4) * 1.6;
        const drift = index % 2 === 0 ? 18 + (index % 5) * 5 : -18 - (index % 5) * 5;

        return `<i class="vault-massive-loot-particle" style="--vault-loot-x: ${x}%; --vault-loot-y: ${y}%; --vault-loot-size: ${size}px; --vault-loot-drift: ${drift}px; --vault-loot-delay: ${index * 86}ms;"></i>`;
    }).join("");

    return `
        <div class="vault-massive-loot-reveal" role="status" aria-live="polite">
            <div class="vault-massive-loot-particles" aria-hidden="true">${particles}</div>
            <span>${escapeHtml(t("vaultMassiveLootLabel"))}</span>
            <strong>x${escapeHtml(String(normalizedPointMultiplier))}!</strong>
        </div>
    `;
};

const renderVaultGrowingStashReveal = (growingStashBonusPoints: number | undefined): string => {
    const normalizedGrowingStashBonusPoints = normalizeGrowingStashBonusPoints(growingStashBonusPoints);
    const revealDeltaPoints = getVaultGrowingStashRevealDeltaPoints(normalizedGrowingStashBonusPoints);

    return `
        <div class="vault-growing-stash-reveal" role="status" aria-live="polite">
            ${renderVaultGrowingStashBills(getVaultBonusStackCount(revealDeltaPoints, VAULT_BREAKER_GROWING_STASH_BONUS_POINTS))}
            <span>${escapeHtml(t("vaultGrowingStashFoundLabel"))}</span>
            <strong>${escapeHtml(
                t("vaultGrowingStashAllVaults", { amount: `+${formatMoney(revealDeltaPoints)}` })
            )}</strong>
        </div>
    `;
};

const formatLockRank = (rank: number): string => {
    const normalizedRank = Math.max(1, Math.trunc(rank));

    if (getLanguage() === "ko") {
        return String(normalizedRank);
    }

    const lastTwoDigits = normalizedRank % 100;
    const suffix =
        lastTwoDigits >= 11 && lastTwoDigits <= 13
            ? "th"
            : normalizedRank % 10 === 1
              ? "st"
              : normalizedRank % 10 === 2
                ? "nd"
                : normalizedRank % 10 === 3
                  ? "rd"
                  : "th";

    return `${normalizedRank}${suffix}`;
};

const formatLockPositionLabel = (rank: number | null | undefined, lockCount: number | null | undefined): string => {
    if (typeof rank !== "number" || !Number.isFinite(rank)) {
        return "";
    }

    const normalizedRank = Math.max(1, Math.trunc(rank));
    const normalizedCount = typeof lockCount === "number" && Number.isFinite(lockCount) ? Math.max(0, Math.trunc(lockCount)) : 0;
    const rankLabel = formatLockRank(normalizedRank);

    return normalizedCount >= normalizedRank
        ? t("vaultLockPositionOf", { rank: rankLabel, count: String(normalizedCount) })
        : t("vaultLockPosition", { rank: rankLabel });
};

const formatLockPositionCompactLabel = (rank: number | null | undefined, lockCount: number | null | undefined): string => {
    if (typeof rank !== "number" || !Number.isFinite(rank)) {
        return "";
    }

    const normalizedRank = Math.max(1, Math.trunc(rank));
    const rawLockCount = typeof lockCount === "number" && Number.isFinite(lockCount) ? Math.trunc(lockCount) : normalizedRank;
    const normalizedCount = Math.max(normalizedRank, rawLockCount);

    return t("vaultLockPositionCompact", {
        rank: String(normalizedRank),
        count: String(normalizedCount)
    });
};

const formatVaultAwardQuickLockDetail = (award: VaultBreakerAwardView): string => {
    if (award.lockRank === null) {
        return "";
    }

    const lockRank = Math.max(1, Math.trunc(award.lockRank));
    const rawLockCount = typeof award.lockCount === "number" && Number.isFinite(award.lockCount) ? Math.trunc(award.lockCount) : lockRank;
    const lockCount = Math.max(lockRank, rawLockCount);

    return t("vaultAwardQuickLockRank", {
        rank: formatLockRank(lockRank),
        count: String(lockCount)
    });
};

const shouldShowStandaloneRank = (award: VaultBreakerAwardView): boolean =>
    award.lockRank !== null && normalizeAwardPoints(award.earlyBonusPoints) <= 0;

const getVaultAwardBreakdownItems = (award: VaultBreakerAwardView): VaultAwardBreakdownItem[] => {
    if (award.correct) {
        const earlyBonusPoints = normalizeAwardPoints(award.earlyBonusPoints);
        const comebackBonusPoints = normalizeAwardPoints(award.comebackBonusPoints);
        const growingStashBonusPoints = normalizeAwardPoints(award.growingStashBonusPoints);
        const diamondBonusPoints = normalizeAwardPoints(award.diamondBonusPoints);
        const pointMultiplier = normalizePointMultiplier(award.pointMultiplier);

        const items: Array<VaultAwardBreakdownItem | null> = [
            normalizeAwardPoints(award.basePoints) > 0
                ? {
                      label: award.finalVault
                          ? t("vaultAwardFinalLabel")
                          : pointMultiplier > 1
                            ? t("vaultAwardMassiveLootLabel")
                            : t("vaultAwardBaseLabel"),
                      points: normalizeAwardPoints(award.basePoints),
                      tone: award.finalVault || pointMultiplier > 1 ? ("loot" as const) : ("vault" as const)
                  }
                : null,
            growingStashBonusPoints > 0
                ? {
                      label: t("vaultGrowingStashLabel"),
                      points: growingStashBonusPoints,
                      tone: "stash" as const
                  }
                : null,
            diamondBonusPoints > 0
                ? {
                      label: t("vaultAwardDiamondLabel"),
                      points: diamondBonusPoints,
                      tone: "diamond" as const
                  }
                : null,
            earlyBonusPoints > 0 || comebackBonusPoints > 0
                ? {
                      label: t("vaultAwardQuickLabel"),
                      points: earlyBonusPoints,
                      tone: "quick" as const,
                      detailLabel: earlyBonusPoints > 0 ? formatVaultAwardQuickLockDetail(award) : undefined,
                      subLabel: comebackBonusPoints > 0 ? t("vaultAwardComebackLabel") : undefined,
                      subPoints: comebackBonusPoints
                  }
                : null
        ];

        return items.filter((item): item is VaultAwardBreakdownItem => item !== null);
    }

    const effortItems: Array<VaultAwardBreakdownItem | null> = [
        normalizeAwardPoints(award.effortPoints) > 0
            ? {
                  label: t("vaultAwardEffortLabel"),
                  points: normalizeAwardPoints(award.effortPoints),
                  tone: "effort" as const
              }
            : null
    ];

    return effortItems.filter((item): item is VaultAwardBreakdownItem => item !== null);
};

const renderVaultAwardBreakdownBadges = (items: readonly VaultAwardBreakdownItem[]): string =>
    items
        .map((item) => {
            const subPoints = normalizeAwardPoints(item.subPoints);
            const hasDetail = Boolean(item.detailLabel);
            const hasSubaward = subPoints > 0 && Boolean(item.subLabel);
            const hasStackedDetails = hasDetail || hasSubaward;
            const valueLabel = item.valueLabel?.trim() || formatMoneyDelta(item.points);

            return `
                <span class="vault-score-chip is-${escapeHtml(item.tone)}${hasStackedDetails ? " has-subaward" : ""}">
                    <span class="vault-score-chip-line">
                        <span>${escapeHtml(item.label)}</span>
                        <strong>${escapeHtml(valueLabel)}</strong>
                    </span>
                    ${
                        item.detailLabel
                            ? `
                                <span class="vault-score-chip-line is-detail">
                                    ${escapeHtml(item.detailLabel)}
                                </span>
                            `
                            : ""
                    }
                    ${
                        hasSubaward && item.subLabel
                            ? `
                                <span class="vault-score-chip-line is-subaward">
                                    <span>${escapeHtml(item.subLabel)}</span>
                                    <strong>${escapeHtml(formatMoneyDelta(subPoints))}</strong>
                                </span>
                            `
                            : ""
                    }
                </span>
            `;
        })
        .join("");

const getVaultAwardBreakdownClass = (baseClass: string, items: readonly VaultAwardBreakdownItem[]): string => {
    const hasLoot = items.some((item) => item.tone === "loot");
    const hasDiamond = items.some((item) => item.tone === "diamond");
    const hasStash = items.some((item) => item.tone === "stash");

    return `${baseClass}${[hasLoot, hasDiamond, hasStash].filter(Boolean).length > 1 ? " has-special-stack" : ""}`;
};

const formatSelectedChoiceLockRank = (choice: VaultBreakerChoiceView | null): string =>
    choice?.lockRank !== null && choice?.lockRank !== undefined ? formatLockPositionLabel(choice.lockRank, choice.lockCount) : "";

const renderVaultTeamLockPositionBadge = (team: VaultBreakerTeamView): string => {
    const compactLabel = formatLockPositionCompactLabel(team.selectedLockRank, team.selectedLockCount);

    if (!compactLabel) {
        return "";
    }

    const fullLabel = formatLockPositionLabel(team.selectedLockRank, team.selectedLockCount);

    return `<span class="vault-team-lock-position" title="${escapeHtml(fullLabel)}" aria-label="${escapeHtml(
        fullLabel
    )}">${escapeHtml(compactLabel)}</span>`;
};

const formatCountdown = (targetTime: number | null, now: number): string => {
    if (targetTime === null) {
        return "--";
    }

    return String(Math.max(0, Math.ceil((targetTime - now) / 1000)));
};

type VaultTimerTone = "idle" | "loot" | "paused" | "cracking" | "resolving" | "success" | "ready" | "failed";

type VaultTimerDisplay = {
    stage: string;
    time: string;
    fillPercent: string;
    tone: VaultTimerTone;
    tickSegments?: number;
    pulseOffsetMs?: number;
};

const VAULT_TIMER_TONES: readonly VaultTimerTone[] = ["idle", "loot", "paused", "cracking", "resolving", "success", "ready", "failed"];

const formatTimerFillPercent = (startTime: number | null, endTime: number | null, now: number): string => {
    if (startTime === null || endTime === null || endTime <= startTime) {
        return "0%";
    }

    const remaining = Math.max(0, Math.min(endTime - startTime, endTime - now));
    const percent = (remaining / (endTime - startTime)) * 100;

    return `${Number(percent.toFixed(2))}%`;
};

const getVaultTimerPulseOffsetMs = (endTime: number | null, now: number): number => {
    if (endTime === null) {
        return 0;
    }

    const remainingMs = Math.max(0, endTime - now);
    return Math.trunc((1000 - (remainingMs % 1000)) % 1000);
};

const getDisplayedCrackDurationSeconds = (vault: VaultBreakerRoomView, crackNumber: VaultBreakerCrackNumber): number => {
    // Use the authoritative interval when available, including the finale's
    // longer crack. Its preparation face and tick marks must match that clock.
    if (crackNumber === vault.crackNumber && vault.crackStartedAt !== null && vault.crackEndsAt !== null) {
        return Math.max(1, Math.round((vault.crackEndsAt - vault.crackStartedAt) / 1000));
    }
    if (vault.finalVaultState === "active" || vault.finalVaultState === "complete") {
        return vault.finalVaultCrackDurationSeconds;
    }
    return getVaultBreakerCrackDurationSeconds(crackNumber, vault.crackDurationSeconds);
};

const formatPausedCrackCountdown = (vault: VaultBreakerRoomView): string =>
    String(getDisplayedCrackDurationSeconds(vault, vault.crackNumber ?? 1));

const getVaultTimerTickSegments = (vault: VaultBreakerRoomView, crackNumber: VaultBreakerCrackNumber = vault.crackNumber ?? 1): number =>
    getDisplayedCrackDurationSeconds(vault, crackNumber);

const getVaultTimerDisplay = (vault: VaultBreakerRoomView, now: number): VaultTimerDisplay => {
    if (!vault.currentVault) {
        return {
            stage: t("waiting"),
            time: `${vault.clearedCount}/${vault.entryCount}`,
            fillPercent: "0%",
            tone: "idle"
        };
    }

    if (isGrowingStashRevealActive(vault, now)) {
        return {
            stage: t("vaultGrowingStashFoundLabel"),
            time: `+${formatMoney(getVaultGrowingStashRevealDeltaPoints(vault.growingStashBonusPoints))}!`,
            fillPercent: formatTimerFillPercent(
                (vault.growingStashRevealEndsAt ?? now) - VAULT_BREAKER_GROWING_STASH_REVEAL_MS,
                vault.growingStashRevealEndsAt,
                now
            ),
            tone: "loot",
            tickSegments: getVaultTimerTickSegments(vault)
        };
    }

    if (isMassiveLootRevealActive(vault, now)) {
        return {
            stage: t("vaultMassiveLootLabel"),
            time: `x${normalizePointMultiplier(vault.pointMultiplier)}!`,
            fillPercent: formatTimerFillPercent(
                (vault.massiveLootRevealEndsAt ?? now) - VAULT_BREAKER_MASSIVE_LOOT_REVEAL_MS,
                vault.massiveLootRevealEndsAt,
                now
            ),
            tone: "loot",
            tickSegments: getVaultTimerTickSegments(vault)
        };
    }

    if (vault.roundComplete) {
        const vaultCracked = vault.teams.some((team) => team.solved);

        return {
            stage: vaultCracked ? localizeRuntimeText("Vault cracked.") : t("vaultDidntCrack"),
            time: vaultCracked ? formatMoney(getVaultDisplayValue(vault.currentVault)) : "X",
            fillPercent: "100%",
            tone: vaultCracked ? "success" : "failed",
            tickSegments: getVaultTimerTickSegments(vault)
        };
    }

    if (vault.armingEndsAt !== null && now < vault.armingEndsAt) {
        return {
            stage: t("vaultScanner"),
            time: formatPausedCrackCountdown(vault),
            fillPercent: "100%",
            tone: "paused",
            tickSegments: getVaultTimerTickSegments(vault)
        };
    }

    if (vault.crackStartedAt !== null && now < vault.crackStartedAt) {
        return {
            stage: t("vaultScanner"),
            time: formatPausedCrackCountdown(vault),
            fillPercent: "100%",
            tone: "paused",
            tickSegments: getVaultTimerTickSegments(vault)
        };
    }

    if (vault.crackStartedAt !== null) {
        const isResolving =
            vault.crackEndsAt !== null && now >= vault.crackEndsAt && (vault.crackResolveAt === null || now < vault.crackResolveAt);

        return {
            stage: t("vaultCrack", { number: String(vault.crackNumber ?? 1) }),
            time: formatCountdown(vault.crackEndsAt, now),
            fillPercent: formatTimerFillPercent(vault.crackStartedAt, vault.crackEndsAt, now),
            tone: isResolving ? "resolving" : "cracking",
            tickSegments: getVaultTimerTickSegments(vault),
            pulseOffsetMs: getVaultTimerPulseOffsetMs(vault.crackEndsAt, now)
        };
    }

    return {
        stage: t("vaultAlarm"),
        time: vault.crackNumber !== null && vault.crackNumber < 3 ? t("vaultReady") : t("vaultDone"),
        fillPercent: vault.crackNumber !== null && vault.crackNumber < 3 ? "0%" : "100%",
        tone: vault.crackNumber !== null && vault.crackNumber < 3 ? "ready" : "success",
        tickSegments: getVaultTimerTickSegments(vault)
    };
};

const normalizeVaultTimerTickSegments = (tickSegments: number | undefined): number =>
    typeof tickSegments === "number" && Number.isFinite(tickSegments) ? Math.max(0, Math.trunc(tickSegments)) : 0;

const isVaultTimerSecondPulseActive = (timer: VaultTimerDisplay): boolean =>
    timer.tone === "cracking" && /^\d+$/.test(timer.time);

const renderVaultTimerRing = (timer: VaultTimerDisplay, className = "", handoffKey = ""): string => {
    const tickSegments = normalizeVaultTimerTickSegments(timer.tickSegments);
    const tickClass = tickSegments > 1 ? "has-ticks" : "";
    const tickStyle = tickSegments > 1 ? ` --vault-timer-segments: ${tickSegments};` : "";
    const pulseActive = isVaultTimerSecondPulseActive(timer);
    const pulseClass = pulseActive ? "is-second-pulse" : "";
    const moneyValueClass = /^[+-]?\$/.test(timer.time) ? "is-money-value" : "";
    const pulseStyle = pulseActive ? ` --vault-timer-pulse-delay: -${timer.pulseOffsetMs ?? 0}ms;` : "";
    const classes = ["vault-timer-ring", timer.tone, className, tickClass, pulseClass, moneyValueClass]
        .filter(Boolean)
        .map((value) => escapeHtml(value))
        .join(" ");

    return `
    <span class="${classes}" style="--vault-timer-fill: ${escapeHtml(timer.fillPercent)};${tickStyle}${pulseStyle}" aria-hidden="true">
        <span>${escapeHtml(timer.time)}</span>
        ${handoffKey ? `<i class="vault-crack-handoff" data-vault-crack-handoff="${escapeHtml(handoffKey)}" aria-hidden="true"></i>` : ""}
    </span>
`;
};

const VAULT_BREAKER_CRACK_COUNT = 3;
const VAULT_TEAM_SCORE_POPUP_MS = 1800;
const VAULT_CHOICE_SCROLL_RESTORE_MS = 180;
const VAULT_CHOOSER_SPOTLIGHT_DELAY_MS = 350;
const VAULT_CHOOSER_SPOTLIGHT_MS = 4200;
const VAULT_CHOOSER_SPOTLIGHT_FADE_IN_MS = 700;
const VAULT_CHOOSER_SPOTLIGHT_FADE_MS = 1400;
const VAULT_DIAMOND_SPOTLIGHT_MS = 3000;
const VAULT_DIAMOND_SPOTLIGHT_FADE_MS = 900;
const VAULT_CHOOSER_SPOTLIGHT_DISMISS_MS = 360;
const VAULT_DIAMOND_COLUMN_GLEAM_MS = 2400;
const VAULT_AUTO_ADVANCE_BACKUP_DELAY_MS = 650;
const VAULT_SURFACE_SETTLE_MS = SITE_TUNING.vaultBreakers.motion.surfaceSettleMs;

type VaultChooserSpotlightKind = "chooser" | "diamond";

const getVaultChooserSpotlightFadeMs = (kind: VaultChooserSpotlightKind): number =>
    kind === "diamond" ? VAULT_DIAMOND_SPOTLIGHT_FADE_MS : VAULT_CHOOSER_SPOTLIGHT_FADE_MS;

const getVaultChooserSpotlightHoldMs = (kind: VaultChooserSpotlightKind): number =>
    kind === "diamond" ? VAULT_DIAMOND_SPOTLIGHT_MS : VAULT_CHOOSER_SPOTLIGHT_MS;

const getVaultChooserSpotlightTotalMs = (kind: VaultChooserSpotlightKind): number =>
    VAULT_CHOOSER_SPOTLIGHT_DELAY_MS + getVaultChooserSpotlightHoldMs(kind) + getVaultChooserSpotlightFadeMs(kind);

const getVaultChooserSequenceWindowMs = (): number =>
    getVaultChooserSpotlightTotalMs("diamond") + getVaultChooserSpotlightTotalMs("chooser") + 2000;

type VaultChooserSpotlightState = {
    key: string;
    kind: VaultChooserSpotlightKind;
    name: string | null;
    queuedChooserName: string | null;
    startedAt: number;
    dismissStartedAt: number | null;
    dismissFromOpacity: number;
    diamondCategoryTitle: string | null;
    diamondAwardedPoints: number;
    diamondTotalPoints: number;
};

const getVaultCrackStatus = (vault: VaultBreakerRoomView, crackNumber: number): VaultBreakerCrackStatus => {
    const crack = vault.cracks?.find((candidate) => candidate.crackNumber === crackNumber);

    if (crack) {
        return crack.status;
    }

    const currentCrack = vault.crackNumber ?? 1;
    const isCurrent = !vault.roundComplete && vault.crackNumber === crackNumber;
    const isDone = vault.roundComplete ? crackNumber <= currentCrack : crackNumber < currentCrack;

    return isCurrent ? "active" : isDone ? "cracked" : "locked";
};

const getVaultFailedPauseCrackNumber = (vault: VaultBreakerRoomView, now: number): number | null => {
    const viewer = vault.viewer;

    if (!viewer || viewer.solved || vault.roundComplete || vault.currentVault === null || vault.crackNumber === null) {
        return null;
    }

    if (
        vault.crackStartedAt !== null &&
        now < vault.crackStartedAt &&
        vault.crackNumber > 1 &&
        viewer.submittedCracks.includes(vault.crackNumber - 1)
    ) {
        return vault.crackNumber - 1;
    }

    if (vault.crackStartedAt === null && viewer.submittedCracks.includes(vault.crackNumber)) {
        return vault.crackNumber;
    }

    if (
        vault.crackStartedAt !== null &&
        vault.crackEndsAt !== null &&
        now >= (vault.crackResolveAt ?? vault.crackEndsAt) &&
        viewer.submittedCracks.includes(vault.crackNumber)
    ) {
        return vault.crackNumber;
    }

    return null;
};

type VaultPlayerStatusTone = "info" | "wrong";

type VaultPlayerStatus = {
    label: string;
    tone: VaultPlayerStatusTone;
};

const getVaultPlayerStatus = (vault: VaultBreakerRoomView, now: number): VaultPlayerStatus | null => {
    const viewer = vault.viewer;

    if (!viewer || vault.currentVault === null || vault.roundComplete || vault.crackNumber === null) {
        return null;
    }

    if (getVaultFailedPauseCrackNumber(vault, now) !== null) {
        return {
            label: t("vaultDidntCrack"),
            tone: "wrong"
        };
    }

    const submittedCrackNumbers = viewer.submittedCracks.filter((crackNumber) => Number.isFinite(crackNumber));
    const lastSubmittedCrack = submittedCrackNumbers.length ? Math.max(...submittedCrackNumbers) : null;
    const submittedRecentFailedCrack =
        lastSubmittedCrack !== null && (lastSubmittedCrack === vault.crackNumber || lastSubmittedCrack === vault.crackNumber - 1);
    const hasUnreplacedFailedAttempt =
        viewer.selectedChoiceId === null &&
        submittedRecentFailedCrack &&
        (viewer.lastFeedbackKind === "wrong" || viewer.lastFeedback === "No code armed." || viewer.lastAward?.correct === false);

    if (hasUnreplacedFailedAttempt) {
        return {
            label: t("vaultDidntCrack"),
            tone: "wrong"
        };
    }

    const waitingForCrackStart = vault.crackStartedAt !== null && now < vault.crackStartedAt;
    const resolvingCurrentCrack =
        vault.crackStartedAt !== null &&
        vault.crackEndsAt !== null &&
        now >= vault.crackEndsAt &&
        (vault.crackResolveAt === null || now < vault.crackResolveAt);
    const waitingForHostAdvance = vault.crackStartedAt === null && vault.roundCompletedAt === null;
    const staleNoCodeFeedback = viewer.lastFeedback === "No code armed." && viewer.selectedChoiceId === null;

    if (waitingForCrackStart || resolvingCurrentCrack || waitingForHostAdvance || staleNoCodeFeedback) {
        return {
            label: t("vaultPlayerStatusScoping"),
            tone: "info"
        };
    }

    return null;
};

const getVaultPlayerStatusBoundaryKey = (vault: VaultBreakerRoomView, now: number): string =>
    getVaultPlayerStatus(vault, now)?.label ?? "no-player-status";

const isVaultPlayerChoiceHold = (room: RoomView, vault: VaultBreakerRoomView, now: number): boolean =>
    !room.viewer.isHost &&
    Boolean(vault.viewer) &&
    vault.currentVault !== null &&
    !vault.roundComplete &&
    !isGrowingStashRevealActive(vault, now) &&
    !isMassiveLootRevealActive(vault, now) &&
    !Boolean(vault.viewer?.canSelect);

type VaultTeamStatusTone = "armed" | "away" | "closed" | "cracking" | "done" | "failed" | "left" | "logged";

type VaultTeamStatus = {
    label: string;
    tone: VaultTeamStatusTone;
};

type VaultTeamScorePopupState = {
    points: number;
    tone: "crack" | "effort";
    sequence: number;
    startedAt: number;
    expiresAt: number;
};

const getMaxValueIndex = (tiles: readonly VaultBreakerTileView[]): number =>
    tiles.reduce((maximum, tile) => Math.max(maximum, tile.valueIndex), 0);

const getRemainingVaultCount = (vault: VaultBreakerRoomView): number =>
    typeof vault.remainingVaultCount === "number" && Number.isFinite(vault.remainingVaultCount)
        ? Math.max(0, Math.trunc(vault.remainingVaultCount))
        : Math.max(0, vault.entryCount - vault.clearedCount);

const isVaultBankHeisted = (vault: VaultBreakerRoomView): boolean => vault.entryCount > 0 && getRemainingVaultCount(vault) === 0;

const isAwaitingFirstVaultRoll = (vault: VaultBreakerRoomView): boolean =>
    vault.roundNumber === 0 && vault.currentVault === null && vault.chooserSessionId === null && !isVaultBankHeisted(vault);

const renderVaultChooserDial = (): string => `
    <span class="vault-chooser-dial" aria-hidden="true">
        <span class="vault-chooser-dial-face"><span class="vault-chooser-dial-hub"></span></span>
    </span>`;

const getTileBySlot = (tiles: readonly VaultBreakerTileView[]): Map<string, VaultBreakerTileView> =>
    new Map(tiles.map((tile) => [`${tile.categoryIndex}:${tile.valueIndex}`, tile]));

const normalizeSubmittedCracks = (submittedCracks: readonly number[]): Set<number> =>
    new Set(submittedCracks.map((crack) => Math.trunc(crack)).filter((crack) => crack >= 1 && crack <= VAULT_BREAKER_CRACK_COUNT));

const getTeamSolvedCrack = (team: VaultBreakerTeamView): number | null => {
    if (!team.solved) return null;
    // The group's outcome cannot prove this team's success. Older snapshots lack
    // an award, so use their last submitted crack, never every earlier attempt.
    if (team.lastAward) return team.lastAward.correct ? team.lastAward.crackNumber : null;
    return Math.max(0, ...normalizeSubmittedCracks(team.submittedCracks)) || null;
};

const isTeamFailedSubmittedCrack = (team: VaultBreakerTeamView, _vault: VaultBreakerRoomView, crackNumber: number): boolean =>
    normalizeSubmittedCracks(team.submittedCracks).has(crackNumber) && getTeamSolvedCrack(team) !== crackNumber;

const hasTeamFailedSubmittedCrack = (team: VaultBreakerTeamView, vault: VaultBreakerRoomView): boolean =>
    Array.from(normalizeSubmittedCracks(team.submittedCracks)).some((crackNumber) => isTeamFailedSubmittedCrack(team, vault, crackNumber));

const hasTeamLeft = (team: VaultBreakerTeamView): boolean =>
    team.awayReason === "hidden" || team.awayReason === "disconnected";

const getTeamStatus = (team: VaultBreakerTeamView, vault: VaultBreakerRoomView, now: number): VaultTeamStatus | null => {
    if (team.solved) {
        return { label: t("vaultTeamDone"), tone: "done" };
    }

    if (team.isAway) {
        return hasTeamLeft(team)
            ? { label: t("vaultTeamLeft"), tone: "left" }
            : { label: t("vaultTeamAway"), tone: "away" };
    }

    if (!vault.currentVault) {
        return null;
    }

    if (team.selectedChoiceId) {
        return { label: t("vaultTeamArmed"), tone: "armed" };
    }

    if (hasTeamFailedSubmittedCrack(team, vault)) {
        return { label: t("vaultDidntCrack"), tone: "failed" };
    }

    if (vault.roundComplete) {
        return { label: t("vaultTeamClosed"), tone: "closed" };
    }

    if (vault.crackNumber !== null && team.submittedCracks.includes(vault.crackNumber)) {
        return { label: t("vaultTeamLogged"), tone: "logged" };
    }

    if (isMassiveLootRevealActive(vault, now)) {
        return null;
    }

    if (vault.crackStartedAt !== null && now >= vault.crackStartedAt) {
        return { label: t("vaultTeamCracking"), tone: "cracking" };
    }

    return null;
};

const renderTeamCrackMeter = (team: VaultBreakerTeamView, vault: VaultBreakerRoomView): string => {
    if (!vault.currentVault && team.submittedCracks.length === 0 && !team.solved) {
        return "";
    }

    const submittedCracks = normalizeSubmittedCracks(team.submittedCracks);
    const submittedCount = Math.min(VAULT_BREAKER_CRACK_COUNT, submittedCracks.size);
    const label = t("vaultCrackProgress", {
        count: String(submittedCount),
        total: String(VAULT_BREAKER_CRACK_COUNT)
    });

    return `
        <span class="vault-crack-meter" aria-label="${escapeHtml(label)}" title="${escapeHtml(label)}">
            ${Array.from({ length: VAULT_BREAKER_CRACK_COUNT }, (_, index) => {
                const crack = index + 1;
                const isSubmitted = submittedCracks.has(crack);
                const isFailed = isTeamFailedSubmittedCrack(team, vault, crack);
                const isCurrent =
                    vault.currentVault !== null && vault.crackNumber === crack && !team.solved && !isSubmitted && !vault.roundComplete;
                const classes = [
                    "vault-crack-dot",
                    isSubmitted && !isFailed ? "is-submitted" : "",
                    isFailed ? "is-failed" : "",
                    isSubmitted && getTeamSolvedCrack(team) === crack ? "is-solved" : "",
                    isCurrent ? "is-current" : "",
                    isCurrent && team.selectedChoiceId ? "is-armed" : "",
                    !isSubmitted && !isCurrent ? "is-upcoming" : ""
                ]
                    .filter(Boolean)
                    .join(" ");

                return `<span class="${classes}" aria-hidden="true">${crack}</span>`;
            }).join("")}
        </span>
    `;
};

class VaultBreakersViewController implements VaultBreakersView {
    private room: RoomView | null = null;
    private renderTimer: number | null = null;
    private serverOffsetMs = 0;
    private readonly audio = new VaultBreakersAudio();
    private readonly capyRunner: VaultCapyRunner;
    private readonly answerCover: VaultAnswerCover;
    private readonly categoryZoom: VaultCategoryZoom;
    private lastQuestionAudioKey = "";
    private lastMassiveLootAudioKey = "";
    private lastDiamondFoundAudioKey = "";
    private lastStashFoundAudioKey = "";
    private lastCompletedAudioKey = "";
    private lastFeedbackAudioKey = "";
    private lastFailedCrackAudioKey = "";
    private lastChooserSelectedAudioKey = "";
    private lastFinalVaultAudioKey = "";
    private lastCrackSummaryKey = "";
    private lastAutoAdvanceCrackKey = "";
    private lastAwardAnimationKey = "";
    private activeAwardAnimation: { key: string; startedAt: number; award: VaultBreakerAwardView } | null = null;
    private chooserSpotlight: VaultChooserSpotlightState | null = null;
    private pendingChooserSpotlightAt: number | null = null;
    private lastDiamondSpotlightKey = "";
    private diamondColumnGleam: { categoryIndex: number; startedAt: number } | null = null;
    private growingStashRoomCode: string | null = null;
    private lastGrowingStashBonusPoints = 0;
    private pendingGrowingStashIncrease = 0;
    private growingStashSequence = 0;
    private growingStashIncrease: { points: number; startedAt: number; sequence: number } | null = null;
    private teamScoreBySessionId = new Map<string, number>();
    private teamScorePopupBySessionId = new Map<string, VaultTeamScorePopupState>();
    private teamScorePopupSequence = 0;
    private choiceGridScrollRestoreTimer: number | null = null;
    private renderedChoiceGridKey: string | null = null;
    private renderedTimeBoundaryKey: string | null = null;
    private openRosterActionsKey: string | null = null;
    private crackDurationEditorOpen = false;
    private playerHistoryMenuOpen = false;
    private rosterLayoutFrameId: number | null = null;
    private rosterLayoutDirty = false;
    private rosterLayoutKey = "";
    private surfaceMotionKey: string | null = null;
    private surfaceMotionPhase: "idle" | "preparing" | "playing" = "idle";
    private surfaceMotionFrameId: number | null = null;
    private surfaceMotionTimerId: number | null = null;
    private surfaceMotionElement: HTMLElement | null = null;
    private surfaceMotionRequestedAt = 0;
    private surfaceMotionStartedAt = 0;
    private surfaceMotionCrackOnly = false;
    private readonly reducedMotionQuery = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    private surfaceReturn: { vaultId: string; overlay: HTMLElement; cameraStyle: string } | null = null;
    private surfaceExit: { element: HTMLElement; inert: boolean; entryVaultId: string | null } | null = null;
    private surfaceExitFrameId: number | null = null;
    private surfaceExitTimerId: number | null = null;
    private skipNextSurfaceEntrance = false;
    private committingSurfaceExit = false;

    public constructor(
        private readonly root: HTMLElement,
        private readonly options: VaultBreakersViewOptions
    ) {
        this.root.classList.add("vault-view-root");
        this.capyRunner = new VaultCapyRunner(this.root);
        this.answerCover = new VaultAnswerCover(this.root);
        this.categoryZoom = new VaultCategoryZoom(this.root);
        this.syncMotionRootClass();
        this.root.addEventListener("click", this.handleClick);
        this.root.addEventListener("change", this.handleChange);
        this.root.addEventListener("pointerdown", this.handleButtonPressStart);
        this.root.addEventListener("pointerup", this.handleButtonPressEnd);
        this.root.addEventListener("pointercancel", this.handleButtonPressEnd);
        this.root.addEventListener("lostpointercapture", this.handleButtonPressEnd);
        this.root.addEventListener("animationend", this.handleSurfaceMotionEnd);
        this.root.addEventListener("animationcancel", this.handleSurfaceMotionEnd);
        window.addEventListener("resize", this.handleResize);
        this.reducedMotionQuery?.addEventListener?.("change", this.handleMotionPreferenceChange);
        window.addEventListener("blur", this.clearButtonPressState);
    }

    public setRoom(room: RoomView) {
        this.room = room;
        this.serverOffsetMs = typeof room.serverNow === "number" ? room.serverNow - Date.now() : 0;
        this.syncGrowingStashIncrease(room);
        this.logResolvedCrackSummary(room);
        this.syncTeamScorePopups(room.vaultBreakers ?? null, room.viewer.isHost);
        this.syncChooserSpotlight(room.vaultBreakers ?? null, room.viewer.isHost, this.now());
        this.syncAudio(room);
        this.syncRenderTimer();
        this.render();
    }

    private logResolvedCrackSummary(room: RoomView) {
        const vault = room.vaultBreakers;
        if (!room.viewer.isHost || !vault?.currentVault || vault.crackNumber === null) return;
        const crack = vault.roundComplete || vault.crackStartedAt === null ? vault.crackNumber : vault.crackNumber - 1;
        if (crack < 1) return;
        const key = `${room.roomCode}:${vault.roundNumber}:${crack}`;
        if (key === this.lastCrackSummaryKey) return;
        this.lastCrackSummaryKey = key;
        const submitted = vault.teams.filter(team => team.submittedCracks.includes(crack));
        const correct = submitted.filter(team => getTeamSolvedCrack(team) === crack).length;
        const detail = `room=${room.roomCode} round=${vault.roundNumber} crack=${crack} submitted=${submitted.length} correct=${correct} unsuccessful=${submitted.length - correct} away=${vault.teams.filter(team => team.isAway && !hasTeamLeft(team)).length} left=${vault.teams.filter(hasTeamLeft).length}`;
        try {
            if (localStorage.getItem("ziarcadia:presence-debug") !== "0") {
                console.info("[Ziarcadia][VaultBreakers] crack-summary", detail);
                appendCodexDebugLog("vault-breakers", "info", "crack-summary", detail);
            }
        } catch { /* Diagnostics must never interrupt gameplay. */ }
    }

    public destroy() {
        this.root.removeEventListener("click", this.handleClick);
        this.root.removeEventListener("change", this.handleChange);
        this.root.removeEventListener("pointerdown", this.handleButtonPressStart);
        this.root.removeEventListener("pointerup", this.handleButtonPressEnd);
        this.root.removeEventListener("pointercancel", this.handleButtonPressEnd);
        this.root.removeEventListener("lostpointercapture", this.handleButtonPressEnd);
        this.root.removeEventListener("animationend", this.handleSurfaceMotionEnd);
        this.root.removeEventListener("animationcancel", this.handleSurfaceMotionEnd);
        window.removeEventListener("resize", this.handleResize);
        this.reducedMotionQuery?.removeEventListener?.("change", this.handleMotionPreferenceChange);
        window.removeEventListener("blur", this.clearButtonPressState);
        this.capyRunner.destroy();
        this.answerCover.destroy();
        this.categoryZoom.destroy();
        this.clearRenderTimer();
        this.clearRosterLayoutFrame();
        this.clearChoiceGridScrollRestoreTimer();
        this.clearSurfaceMotion();
        this.clearSurfaceExit();
        this.root.style.removeProperty("--vault-translation-opacity");
        this.audio.dispose();
        this.root.classList.remove(
            "vault-view-root",
            "is-scanning",
            "has-automatic-translations",
            "is-vault-resolving",
            "is-vault-choice-hold",
            "is-vault-motion-enabled",
            "is-vault-motion-disabled"
        );
    }

    private readonly clearButtonPressState = () => {
        this.root.querySelectorAll(".vault-choice-button.is-pressing, .vault-tile-button.is-pressing").forEach((button) => {
            button.classList.remove("is-pressing");
        });
    };

    private readonly handleButtonPressStart = (event: PointerEvent) => {
        if (event.pointerType === "mouse" && event.button !== 0) {
            return;
        }

        const target = event.target as HTMLElement | null;
        const button = target?.closest<HTMLButtonElement>(".vault-choice-button, .vault-tile-button");

        if (!button || button.disabled) {
            return;
        }

        this.clearButtonPressState();
        button.classList.add("is-pressing");

        if (event.isTrusted && button.setPointerCapture) {
            button.setPointerCapture(event.pointerId);
        }
    };

    private readonly handleButtonPressEnd = (event: PointerEvent) => {
        const target = event.target as HTMLElement | null;
        const button = target?.closest<HTMLButtonElement>(".vault-choice-button, .vault-tile-button");

        this.clearButtonPressState();

        if (button?.hasPointerCapture?.(event.pointerId)) {
            button.releasePointerCapture(event.pointerId);
        }
    };

    private readonly handleClick = (event: Event) => {
        const target = event.target as HTMLElement | null;

        if (!target) {
            return;
        }

        void this.audio.prepare();

        const dismissSpotlightButton = target.closest<HTMLElement>("[data-vault-dismiss-spotlight]");
        const vaultButton = target.closest<HTMLButtonElement>("[data-vault-id]");
        const choiceButton = target.closest<HTMLButtonElement>("[data-vault-choice-id]");
        const endGameButton = target.closest<HTMLButtonElement>("[data-end-game]");
        const nextCrackButton = target.closest<HTMLButtonElement>("[data-vault-next-crack]");
        const finishRoundButton = target.closest<HTMLButtonElement>("[data-vault-finish-round]");
        const rollChooserButton = target.closest<HTMLButtonElement>("[data-vault-roll]");
        const toggleFinalVaultButton = target.closest<HTMLButtonElement>("[data-vault-toggle-final]");
        const showChooserButton = target.closest<HTMLButtonElement>("[data-vault-show-chooser]");
        const unlockNextValueButton = target.closest<HTMLButtonElement>("[data-vault-unlock-next-value]");
        const crackDurationToggleButton = target.closest<HTMLButtonElement>("[data-vault-crack-duration-toggle]");
        const crackDurationStepButton = target.closest<HTMLButtonElement>("[data-vault-crack-duration-step]");
        const crackDurationEditor = target.closest<HTMLElement>("[data-vault-crack-duration-editor]");
        const playerHistoryMenu = target.closest<HTMLElement>("[data-player-history-menu]");
        const playerHistoryMenuToggle = target.closest<HTMLElement>("[data-player-history-menu-toggle]");
        const playerHistoryDownloadButton = target.closest<HTMLElement>("[data-player-history-download]");
        const scoreButton = target.closest<HTMLButtonElement>("[data-vault-score-session]");
        const promptRenameButton = target.closest<HTMLButtonElement>("[data-vault-prompt-rename]");
        const kickButton = target.closest<HTMLButtonElement>("[data-vault-kick]");
        const rosterActionsToggle = target.closest<HTMLElement>('[data-action="toggle-roster-actions"]');
        const clickedRosterTransientControl = Boolean(
            playerHistoryMenu ||
            playerHistoryDownloadButton ||
            rosterActionsToggle ||
            scoreButton ||
            promptRenameButton ||
            kickButton ||
            showChooserButton ||
            crackDurationToggleButton ||
            crackDurationEditor
        );

        if (dismissSpotlightButton) {
            event.preventDefault();
            event.stopPropagation();
            this.dismissChooserSpotlight(dismissSpotlightButton.closest<HTMLElement>(".vault-chooser-spotlight"));
            return;
        }

        if (this.playerHistoryMenuOpen && !playerHistoryMenu) {
            this.playerHistoryMenuOpen = false;
            this.render();
        }

        if (!clickedRosterTransientControl && (this.openRosterActionsKey || this.crackDurationEditorOpen)) {
            this.openRosterActionsKey = null;
            this.crackDurationEditorOpen = false;
            this.render();
        }

        if (playerHistoryMenuToggle) {
            event.preventDefault();
            event.stopPropagation();
            this.playerHistoryMenuOpen = !this.playerHistoryMenuOpen;
            this.openRosterActionsKey = null;
            this.crackDurationEditorOpen = false;
            this.render();
            return;
        }

        if (playerHistoryDownloadButton) {
            event.preventDefault();
            event.stopPropagation();
            this.playerHistoryMenuOpen = false;
            if (this.room) {
                downloadPlayerHistory(this.room);
            }
            this.render();
            return;
        }

        if (endGameButton) {
            return;
        }

        if (vaultButton) {
            const vaultId = vaultButton.getAttribute("data-vault-id") ?? "";
            if (vaultId) {
                this.options.onChooseVault(vaultId);
            }
            return;
        }

        if (choiceButton) {
            if (choiceButton.disabled) {
                return;
            }

            const choiceId = choiceButton.getAttribute("data-vault-choice-id") ?? "";
            if (choiceId) {
                if ((event as MouseEvent).detail > 0) choiceButton.blur();

                this.options.onSelectChoice(choiceId);
            }
            return;
        }

        if (nextCrackButton) {
            const identity = getVaultBreakerCrackAdvanceIdentity(this.room);

            if (identity) {
                this.options.onNextCrack(identity);
            }
            return;
        }

        if (finishRoundButton) {
            this.options.onFinishRound();
            return;
        }

        if (toggleFinalVaultButton) {
            this.options.onToggleFinalVault?.();
            return;
        }

        if (unlockNextValueButton) {
            event.preventDefault();

            if (!unlockNextValueButton.disabled) {
                this.options.onUnlockNextValue?.();
            }
            return;
        }

        if (rosterActionsToggle) {
            event.preventDefault();
            event.stopPropagation();
            const actionsKey = rosterActionsToggle.getAttribute("data-roster-action-key") ?? "";
            this.openRosterActionsKey = actionsKey && this.openRosterActionsKey !== actionsKey ? actionsKey : null;
            this.crackDurationEditorOpen = false;
            this.playerHistoryMenuOpen = false;
            this.render();
            return;
        }

        if (crackDurationToggleButton) {
            event.preventDefault();
            event.stopPropagation();
            this.crackDurationEditorOpen = !this.crackDurationEditorOpen;
            this.openRosterActionsKey = null;
            this.render();

            if (this.crackDurationEditorOpen) {
                const input = this.root.querySelector<HTMLInputElement>("[data-vault-crack-duration-input]");
                input?.focus();
                input?.select();
            }
            return;
        }

        if (showChooserButton && !showChooserButton.disabled) {
            event.preventDefault();
            event.stopPropagation();
            this.openRosterActionsKey = null;
            this.crackDurationEditorOpen = false;
            this.playerHistoryMenuOpen = false;
            this.showChooserSpotlight(this.room?.vaultBreakers?.chooserName?.trim() ?? "");
            return;
        }

        if (rollChooserButton && !rollChooserButton.disabled) {
            this.pendingChooserSpotlightAt = this.now();
            this.options.onRollChooser();
            return;
        }

        if (crackDurationStepButton) {
            event.preventDefault();
            const vault = this.room?.vaultBreakers ?? null;
            const delta = Number.parseInt(crackDurationStepButton.getAttribute("data-vault-crack-duration-step") ?? "0", 10);

            if (vault && Number.isFinite(delta) && delta !== 0) {
                this.crackDurationEditorOpen = true;
                this.options.onAdjustCrackDuration?.(clampVaultBreakerCrackDurationSeconds(vault.crackDurationSeconds + delta));
            }
            return;
        }

        if (scoreButton) {
            const sessionId = scoreButton.getAttribute("data-vault-score-session") ?? "";
            const delta = Number.parseInt(scoreButton.getAttribute("data-vault-score-delta") ?? "0", 10);
            this.openRosterActionsKey = null;
            this.render();
            if (sessionId && Number.isFinite(delta) && delta !== 0) {
                this.options.onAdjustScore(sessionId, delta);
            }
            return;
        }

        if (promptRenameButton) {
            const sessionId = promptRenameButton.getAttribute("data-vault-prompt-rename") ?? "";
            this.openRosterActionsKey = null;
            this.render();
            if (sessionId) {
                this.options.onPromptRenameParticipant(sessionId);
            }
            return;
        }

        if (kickButton) {
            const sessionId = kickButton.getAttribute("data-vault-kick") ?? "";
            this.openRosterActionsKey = null;
            this.render();
            if (sessionId) {
                this.options.onKickParticipant(sessionId);
            }
        }
    };

    private readonly handleResize = () => {
        if (this.surfaceReturn) this.clearSurfaceMotion();
        this.scheduleRosterLayoutSync();
    };

    private readonly handleMotionPreferenceChange = () => {
        // A paused/prepared CSS animation can disappear without an end event.
        // Commit the current room when the OS preference changes as well.
        this.render();
    };

    private syncMotionRootClass() {
        const motionEnabled = SITE_TUNING.vaultBreakers.motion.enabled;

        this.root.classList.toggle("is-vault-motion-enabled", motionEnabled);
        this.root.classList.toggle("is-vault-motion-disabled", !motionEnabled);
    }

    private readonly handleChange = (event: Event) => {
        const target = event.target as HTMLElement | null;
        const input = target?.closest<HTMLInputElement>("[data-vault-crack-duration-input]");

        if (!input) {
            return;
        }

        const nextDurationSeconds = clampVaultBreakerCrackDurationSeconds(Number.parseInt(input.value, 10));
        input.value = String(nextDurationSeconds);
        this.options.onAdjustCrackDuration?.(nextDurationSeconds);
    };

    private syncTranslationFade(room: RoomView, vault: VaultBreakerRoomView, now: number) {
        const available = !room.viewer.isHost && hasVaultBreakerSupplementalInfo(vault.viewer?.choices ?? []);
        const opacity = available && !isGrowingStashRevealActive(vault, now) && !isMassiveLootRevealActive(vault, now)
            ? typeof vault.viewer?.translationDurationFraction === "number"
                ? getVaultPersonalTranslationOpacity(vault.crackStartedAt, vault.crackEndsAt, now, vault.viewer.translationDurationFraction)
                : getVaultTranslationOpacity(vault.crackStartedAt, vault.crackEndsAt, now,
                    vault.viewer?.translationRankFraction, SITE_TUNING.vaultBreakers.translations.leaderDurationFraction)
            : 0;
        this.root.classList.toggle("has-automatic-translations", available);
        if (this.root.style.getPropertyValue("--vault-translation-opacity") !== String(opacity)) {
            this.root.style.setProperty("--vault-translation-opacity", String(opacity));
        }
        this.root.querySelectorAll<HTMLElement>(".vault-choice-korean, .vault-selected-code-korean").forEach(element => {
            if (element.getAttribute("aria-hidden") !== String(opacity === 0)) {
                element.setAttribute("aria-hidden", String(opacity === 0));
            }
        });
    }

    private clearChoiceGridScrollRestoreTimer() {
        if (this.choiceGridScrollRestoreTimer !== null) {
            window.clearTimeout(this.choiceGridScrollRestoreTimer);
            this.choiceGridScrollRestoreTimer = null;
        }
    }

    private clearRosterLayoutFrame() {
        if (this.rosterLayoutFrameId !== null) {
            window.cancelAnimationFrame(this.rosterLayoutFrameId);
            this.rosterLayoutFrameId = null;
        }
    }

    private scheduleRosterLayoutSync(remeasure = true) {
        if (!this.room?.viewer.isHost) {
            this.clearRosterLayoutFrame();
            return;
        }

        this.rosterLayoutDirty ||= remeasure;
        if (this.rosterLayoutFrameId !== null) {
            return;
        }

        const sync = () => {
            this.rosterLayoutFrameId = null;
            positionOpenPlayerRosterActionsMenus(this.root);
            if (!this.rosterLayoutDirty) return;
            this.rosterLayoutDirty = false;
            syncPlayerRosterLayout({
                rosterBody: this.root.querySelector<HTMLElement>(".vault-roster-card .roster-body"),
                rosterList: this.root.querySelector<HTMLElement>(".vault-team-list")
            });
        };

        if (typeof window.requestAnimationFrame === "function") {
            this.rosterLayoutFrameId = window.requestAnimationFrame(sync);
        } else {
            sync();
        }
    }

    private startChooserSpotlight(name: string, key: string, now: number): boolean {
        const chooserName = name.trim();

        if (!chooserName) {
            return false;
        }

        this.pendingChooserSpotlightAt = null;
        this.chooserSpotlight = {
            key,
            kind: "chooser",
            name: chooserName,
            queuedChooserName: null,
            startedAt: now,
            dismissStartedAt: null,
            dismissFromOpacity: 1,
            diamondCategoryTitle: null,
            diamondAwardedPoints: 0,
            diamondTotalPoints: 0
        };
        return true;
    }

    private showChooserSpotlight(name: string) {
        const now = this.now();
        const vault = this.room?.vaultBreakers ?? null;

        if (!this.startChooserSpotlight(name, `chooser:${vault?.roundNumber ?? "round"}:${vault?.chooserSessionId ?? "chooser"}:${now}`, now)) {
            return;
        }

        this.syncRenderTimer();
        this.render();
    }

    private startDiamondSpotlight(
        vault: VaultBreakerRoomView,
        key: string,
        queuedChooserName: string,
        now: number
    ): boolean {
        const diamondAwardedAt =
            typeof vault.diamondAwardedAt === "number" && Number.isFinite(vault.diamondAwardedAt)
                ? vault.diamondAwardedAt
                : null;
        const diamondAwardedPoints =
            typeof vault.diamondAwardedPoints === "number" && Number.isFinite(vault.diamondAwardedPoints)
                ? Math.max(0, Math.trunc(vault.diamondAwardedPoints))
                : 0;
        const diamondTotalPoints =
            typeof vault.diamondTotalPoints === "number" && Number.isFinite(vault.diamondTotalPoints)
                ? Math.max(0, Math.trunc(vault.diamondTotalPoints))
                : 0;
        const diamondCategoryTitle = vault.diamondCategoryTitle?.trim() ?? "";
        const canShowDiamond =
            diamondAwardedAt !== null &&
            diamondAwardedPoints > 0 &&
            diamondCategoryTitle.length > 0 &&
            now - diamondAwardedAt <= getVaultChooserSequenceWindowMs();

        if (!canShowDiamond) {
            return false;
        }

        this.pendingChooserSpotlightAt = null;
        this.chooserSpotlight = {
            key,
            kind: "diamond",
            name: null,
            queuedChooserName: queuedChooserName.trim() || null,
            startedAt: now,
            dismissStartedAt: null,
            dismissFromOpacity: 1,
            diamondCategoryTitle,
            diamondAwardedPoints,
            diamondTotalPoints
        };
        this.diamondColumnGleam =
            typeof vault.diamondCategoryIndex === "number" && Number.isFinite(vault.diamondCategoryIndex)
                ? { categoryIndex: Math.max(0, Math.trunc(vault.diamondCategoryIndex)), startedAt: now }
                : null;
        return true;
    }

    private advanceChooserSpotlight(now: number): boolean {
        const spotlight = this.chooserSpotlight;

        if (!spotlight) {
            return false;
        }

        const complete =
            spotlight.dismissStartedAt !== null
                ? now - spotlight.dismissStartedAt >= VAULT_CHOOSER_SPOTLIGHT_DISMISS_MS
                : now - spotlight.startedAt > getVaultChooserSpotlightTotalMs(spotlight.kind);

        if (!complete) {
            return false;
        }

        if (spotlight.kind === "diamond" && spotlight.queuedChooserName) {
            return this.startChooserSpotlight(spotlight.queuedChooserName, `${spotlight.key}:chooser`, now);
        }

        this.chooserSpotlight = null;
        return true;
    }

    private dismissChooserSpotlight(element: HTMLElement | null) {
        if (!this.chooserSpotlight || this.chooserSpotlight.dismissStartedAt !== null) {
            return;
        }

        const renderedOpacity = element
            ? Number.parseFloat(element.ownerDocument.defaultView?.getComputedStyle(element).opacity ?? "1")
            : 1;
        this.chooserSpotlight.dismissStartedAt = this.now();
        this.chooserSpotlight.dismissFromOpacity = Number.isFinite(renderedOpacity) ? renderedOpacity : 1;
        this.syncRenderTimer();
        this.render();
    }

    private getDiamondSpotlightKey(vault: VaultBreakerRoomView, now: number): string {
        const diamondAwardedAt =
            typeof vault.diamondAwardedAt === "number" && Number.isFinite(vault.diamondAwardedAt)
                ? vault.diamondAwardedAt
                : null;
        const diamondAwardedPoints =
            typeof vault.diamondAwardedPoints === "number" && Number.isFinite(vault.diamondAwardedPoints)
                ? Math.max(0, Math.trunc(vault.diamondAwardedPoints))
                : 0;
        const diamondCategoryTitle = vault.diamondCategoryTitle?.trim() ?? "";

        if (
            diamondAwardedAt === null ||
            diamondAwardedPoints <= 0 ||
            diamondCategoryTitle.length === 0 ||
            now - diamondAwardedAt > getVaultChooserSequenceWindowMs()
        ) {
            return "";
        }

        return [
            vault.roundNumber,
            vault.chooserSessionId ?? "chooser",
            vault.diamondCategoryIndex ?? "category",
            diamondAwardedAt,
            diamondAwardedPoints
        ].join(":");
    }
    private syncChooserSpotlight(vault: VaultBreakerRoomView | null, isHost: boolean, now: number): boolean {
        const getRenderKey = () =>
            this.chooserSpotlight
                ? `${this.chooserSpotlight.key}:${this.chooserSpotlight.kind}:${this.chooserSpotlight.dismissStartedAt ?? "active"}`
                : "";
        const previousKey = getRenderKey();

        if (!isHost || !vault || vault.currentVault !== null) {
            this.pendingChooserSpotlightAt = null;
            this.chooserSpotlight = null;
            return previousKey !== "";
        }

        if (this.pendingChooserSpotlightAt !== null && now - this.pendingChooserSpotlightAt > getVaultChooserSequenceWindowMs()) {
            this.pendingChooserSpotlightAt = null;
        }

        const chooserName = vault.chooserName?.trim() ?? "";
        const diamondSpotlightKey = this.getDiamondSpotlightKey(vault, now);
        if (diamondSpotlightKey && diamondSpotlightKey !== this.lastDiamondSpotlightKey) {
            this.lastDiamondSpotlightKey = diamondSpotlightKey;
            this.startDiamondSpotlight(vault, `diamond:${diamondSpotlightKey}`, chooserName, now);
        } else {
            this.advanceChooserSpotlight(now);

            if (this.pendingChooserSpotlightAt !== null && chooserName) {
                this.startChooserSpotlight(
                    chooserName,
                    `chooser:${vault.roundNumber}:${vault.chooserSessionId ?? "chooser"}:${now}`,
                    now
                );
            }
        }

        return previousKey !== getRenderKey();
    }

    private hasActiveChooserSpotlight(now = this.now()): boolean {
        if (!this.chooserSpotlight) {
            return false;
        }

        return this.chooserSpotlight.dismissStartedAt !== null
            ? now - this.chooserSpotlight.dismissStartedAt <= VAULT_CHOOSER_SPOTLIGHT_DISMISS_MS
            : now - this.chooserSpotlight.startedAt <= getVaultChooserSpotlightTotalMs(this.chooserSpotlight.kind);
    }

    private syncAutoAdvanceCrack(room: RoomView | null, now: number) {
        const vault = room?.vaultBreakers ?? null;
        const identity = getVaultBreakerCrackAdvanceIdentity(room);
        const crackKey =
            identity &&
            vault !== null &&
            identity.crackStartedAt !== null &&
            vault.crackStartedAt !== null &&
            vault.crackEndsAt !== null &&
            vault.roundCompletedAt === null
                ? `${room?.roomCode}:${identity.roundNumber}:${identity.vaultId}:${identity.crackNumber}:${identity.crackStartedAt}`
                : "";

        if (!identity || !crackKey) {
            this.lastAutoAdvanceCrackKey = "";
            return;
        }

        if (now < (vault?.crackResolveAt ?? vault?.crackEndsAt ?? 0) + VAULT_AUTO_ADVANCE_BACKUP_DELAY_MS) {
            return;
        }

        if (this.lastAutoAdvanceCrackKey === crackKey) {
            return;
        }

        this.lastAutoAdvanceCrackKey = crackKey;
        this.options.onNextCrack(identity);
    }

    private getSurfaceMotionKey(room: RoomView, vault: VaultBreakerRoomView, now: number): string {
        const roleKey = room.viewer.isHost ? "host" : "player";

        if (vault.currentVaultId === null) {
            return `${roleKey}:board:${vault.entryCount}:${vault.clearedCount}:${isVaultBankHeisted(vault) ? "heisted" : "open"}`;
        }

        if (isGrowingStashRevealActive(vault, now)) {
            return `${roleKey}:growing-stash:${vault.currentVaultId}`;
        }

        if (isMassiveLootRevealActive(vault, now)) {
            return `${roleKey}:loot:${vault.currentVaultId}`;
        }

        if (vault.roundComplete) {
            const crackStates = vault.cracks.map((crack) => `${crack.crackNumber}-${crack.status}`).join("|");

            return `${roleKey}:result:${vault.currentVaultId}:${vault.crackNumber ?? "none"}:${crackStates}`;
        }

        // The arming window and live countdown are one question surface. Splitting them replays the entrance motion
        // when the timer starts, which makes the newly revealed question appear to bounce a second time.
        return `${roleKey}:question:${vault.currentVaultId}:c${vault.crackNumber ?? "none"}`;
    }

    private syncSurfaceMotion(room: RoomView, vault: VaultBreakerRoomView, now: number) {
        this.syncMotionRootClass();
        const nextSurfaceKey = this.getSurfaceMotionKey(room, vault, now);
        const changed = this.surfaceMotionKey !== nextSurfaceKey;
        const hadSurface = this.surfaceMotionKey !== null;
        const crackOnly = room.viewer.isHost && nextSurfaceKey.includes(":question:") &&
            this.surfaceMotionKey !== nextSurfaceKey &&
            this.surfaceMotionKey?.replace(/:c[123]$/, "") === nextSurfaceKey.replace(/:c[123]$/, "");
        const enabled = this.isSurfaceMotionEnabled();
        if (!changed && enabled) return;
        const returnVaultId = changed && enabled && room.viewer.isHost && nextSurfaceKey.includes(":board:") &&
            this.surfaceMotionKey?.startsWith("host:") && !this.surfaceMotionKey.includes(":board:")
            ? this.surfaceMotionKey.split(":")[2] : null;
        const outgoing = returnVaultId ? this.root.querySelector<HTMLElement>("[data-vault-surface-key]") : null;
        this.clearSurfaceMotion();
        this.surfaceMotionKey = nextSurfaceKey;
        if (changed && hadSurface && enabled && !this.skipNextSurfaceEntrance) {
            this.surfaceMotionPhase = "preparing";
            this.surfaceMotionCrackOnly = crackOnly;
            this.root.classList.add("is-vault-motion-preparing");
            this.surfaceMotionRequestedAt = performance.now();
            if (returnVaultId && outgoing && vault.tiles.some(tile => tile.id === returnVaultId)) {
                this.prepareBoardReturn(outgoing, returnVaultId);
            }
        }
        this.skipNextSurfaceEntrance = false;
    }

    private isSurfaceMotionEnabled(): boolean {
        return SITE_TUNING.vaultBreakers.motion.enabled &&
            !document.documentElement.classList.contains("ziar-vault-motion-disabled") &&
            !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    }

    private holdForSurfaceExit(room: RoomView, vault: VaultBreakerRoomView, now: number): boolean {
        const nextKey = this.getSurfaceMotionKey(room, vault, now);
        const oldKey = this.surfaceMotionKey;
        const sameQuestion = Boolean(oldKey?.includes(":question:") && nextKey.includes(":question:") &&
            oldKey.replace(/:c[123]$/, "") === nextKey.replace(/:c[123]$/, ""));
        // Crack changes keep their question/timer treatment. Reversal or reduced
        // motion cancels the pending exit and commits the latest room immediately.
        if (this.committingSurfaceExit || !oldKey || nextKey === oldKey || sameQuestion ||
            !this.isSurfaceMotionEnabled() || oldKey.split(":")[0] !== nextKey.split(":")[0]) {
            this.clearSurfaceExit();
            return false;
        }
        if (this.surfaceExit) {
            if (!this.surfaceExit.entryVaultId || this.surfaceExit.entryVaultId === vault.currentVaultId) return true;
            this.clearSurfaceExit();
        }
        const surface = this.root.querySelector<HTMLElement>("[data-vault-surface-key]");
        if (!surface || typeof window.requestAnimationFrame !== "function") return false;
        // Returning overlaps the result with the board pullback; a serial exit
        // would fade to an empty panel before the board can start moving.
        if (room.viewer.isHost && nextKey.includes(":board:") && !oldKey.includes(":board:")) return false;
        const style = getComputedStyle(surface);
        const opacity = style.opacity;
        const transform = style.transform;
        this.clearSurfaceMotion();
        this.clearButtonPressState();
        const entry = room.viewer.isHost && oldKey.includes(":board:") && vault.currentVaultId
            ? this.capyRunner.prepareVaultEntry(vault.currentVaultId) : null;
        this.surfaceExit = { element: surface, inert: surface.inert, entryVaultId: entry ? vault.currentVaultId : null };
        surface.inert = true;
        surface.style.setProperty("--vault-surface-exit-start-opacity", opacity);
        surface.style.setProperty("--vault-surface-exit-start-transform", transform);
        surface.classList.add("is-vault-surface-exiting", "is-vault-exit-preparing");
        if (entry) {
            const tile = Array.from(surface.querySelectorAll<HTMLElement>("[data-vault-id]"))
                .find(candidate => candidate.dataset.vaultId === vault.currentVaultId)!;
            surface.style.cssText += this.getVaultCameraStyle(surface, tile);
            tile.classList.add("is-vault-entry-target");
            const opening = document.createElement("span");
            opening.className = "vault-entry-opening";
            opening.setAttribute("aria-hidden", "true");
            tile.append(opening);
            surface.classList.add("is-vault-entry-exiting");
        }
        // Keep the outgoing DOM in place. Room state, audio and server deadlines
        // continue advancing; completion renders the newest snapshot, never a queue.
        this.surfaceExitFrameId = window.requestAnimationFrame(() => {
            this.surfaceExitFrameId = window.requestAnimationFrame(() => {
                this.surfaceExitFrameId = null;
                if (this.surfaceExit?.element !== surface) return;
                if (!surface.isConnected || !this.isSurfaceMotionEnabled()) {
                    this.finishSurfaceExit();
                    return;
                }
                surface.classList.remove("is-vault-exit-preparing");
                const motion = SITE_TUNING.vaultBreakers.motion;
                const expectedMs = entry ? motion.vaultEntryLeapMs + motion.vaultEntryZoomMs : motion.surfaceExitMs;
                this.surfaceExitTimerId = window.setTimeout(() => {
                    if (this.isSurfaceMotionEnabled() && surface.getAnimations().some(animation =>
                        ["vaultEntryZoom", "vaultSurfaceExit"].includes((animation as CSSAnimation).animationName) &&
                        animation.playState === "paused")) return;
                    try {
                        if (localStorage.getItem("ziarcadia:vault-motion-debug") !== "0") {
                            const message = `phase=exit-fallback surface=${oldKey} route=${entry ? "capy-entry" : "scale-fade"} vault=${vault.currentVaultId ?? "none"} expected_ms=${expectedMs}`;
                            console.info(`[Ziarcadia][VaultMotion] ${message}`);
                            appendCodexDebugLog("vault-breakers", "info", message);
                        }
                    } catch { /* Diagnostics must not prevent the latest room from rendering. */ }
                    this.finishSurfaceExit();
                }, expectedMs + 180);
            });
        });
        return true;
    }

    private clearSurfaceExit() {
        if (this.surfaceExitFrameId !== null) window.cancelAnimationFrame(this.surfaceExitFrameId);
        this.surfaceExitFrameId = null;
        if (this.surfaceExitTimerId !== null) window.clearTimeout(this.surfaceExitTimerId);
        this.surfaceExitTimerId = null;
        const exit = this.surfaceExit;
        this.surfaceExit = null;
        if (!exit) return;
        exit.element.inert = exit.inert;
        exit.element.classList.remove("is-vault-surface-exiting", "is-vault-exit-preparing", "is-vault-entry-exiting");
        exit.element.style.removeProperty("--vault-surface-exit-start-opacity");
        exit.element.style.removeProperty("--vault-surface-exit-start-transform");
        exit.element.style.removeProperty("transform-origin");
        for (const property of ["x", "y", "scale"]) exit.element.style.removeProperty(`--vault-camera-${property}`);
        exit.element.querySelector(".is-vault-entry-target")?.classList.remove("is-vault-entry-target");
        exit.element.querySelector(".vault-entry-opening")?.remove();
        this.capyRunner.cancelVaultEntry();
    }

    private finishSurfaceExit() {
        this.skipNextSurfaceEntrance = Boolean(this.surfaceExit?.entryVaultId);
        this.clearSurfaceExit();
        this.committingSurfaceExit = true;
        try { this.render(); } finally { this.committingSurfaceExit = false; }
    }

    private getSurfaceMotionClass(): string {
        return this.surfaceMotionPhase === "idle" ? "" :
            ` is-vault-surface-entering${this.surfaceMotionPhase === "preparing" ? " is-vault-surface-preparing" : ""}${this.surfaceMotionCrackOnly ? " is-vault-crack-only" : ""}${this.surfaceReturn ? " is-vault-board-returning" : ""}`;
    }

    private getVaultCameraStyle(surface: HTMLElement, tile: HTMLElement): string {
        const bounds = surface.getBoundingClientRect();
        const target = tile.getBoundingClientRect();
        const style = getComputedStyle(surface);
        const width = Number.parseFloat(style.width) || surface.offsetWidth;
        const height = Number.parseFloat(style.height) || surface.offsetHeight;
        const x = (target.left + target.width / 2 - bounds.left) / bounds.width;
        const y = (target.top + target.height / 2 - bounds.top) / bounds.height;
        const motion = SITE_TUNING.vaultBreakers.motion;
        const scale = Math.min(motion.vaultEntryZoomMaxScale, Math.max(motion.vaultEntryZoomScale,
            bounds.width / target.width, bounds.height / target.height));
        // Move the selected tile to the panel centre as it fills the camera.
        // Changing transform-origin alone leaves an edge tile at the edge.
        return `transform-origin:50% 50%;--vault-camera-x:${(0.5 - x) * width * scale}px;--vault-camera-y:${(0.5 - y) * height * scale}px;--vault-camera-scale:${scale};`;
    }

    private prepareBoardReturn(outgoing: HTMLElement, vaultId: string) {
        const shell = outgoing.closest<HTMLElement>(".vault-host-shell")!;
        const bounds = outgoing.getBoundingClientRect();
        const parent = shell.getBoundingClientRect();
        const scaleX = parent.width / shell.offsetWidth;
        const scaleY = parent.height / shell.offsetHeight;
        const overlay = outgoing.cloneNode(true) as HTMLElement;
        overlay.className = "vault-return-overlay is-vault-return-preparing";
        overlay.removeAttribute("data-vault-surface-key");
        overlay.querySelectorAll("[id]").forEach(element => element.removeAttribute("id"));
        overlay.inert = true;
        overlay.setAttribute("aria-hidden", "true");
        overlay.style.cssText = `left:${(bounds.left - parent.left) / scaleX - shell.clientLeft}px;top:${(bounds.top - parent.top) / scaleY - shell.clientTop}px;width:${bounds.width / scaleX}px;height:${bounds.height / scaleY}px;`;
        // Keep the snapshot outside the board card: its :has(question) paint
        // rules must already see the incoming board, including cleared vaults.
        shell.append(overlay);
        this.surfaceReturn = { vaultId, overlay, cameraStyle: "" };
    }

    private clearSurfaceMotion() {
        if (this.surfaceMotionFrameId !== null) window.cancelAnimationFrame(this.surfaceMotionFrameId);
        this.surfaceMotionFrameId = null;
        if (this.surfaceMotionTimerId !== null) window.clearTimeout(this.surfaceMotionTimerId);
        this.surfaceMotionTimerId = null;
        this.surfaceMotionElement?.classList.remove("is-vault-surface-entering", "is-vault-surface-preparing", "is-vault-crack-only", "is-vault-board-returning");
        if (this.surfaceReturn) {
            if (this.surfaceMotionElement) {
                this.surfaceMotionElement.inert = false;
                this.surfaceMotionElement.style.removeProperty("transform-origin");
                for (const property of ["x", "y", "scale"]) this.surfaceMotionElement.style.removeProperty(`--vault-camera-${property}`);
            }
            this.surfaceReturn.overlay.remove();
            this.surfaceReturn = null;
        }
        this.root.classList.remove("is-vault-motion-preparing");
        this.surfaceMotionElement = null;
        this.surfaceMotionPhase = "idle";
        this.surfaceMotionCrackOnly = false;
    }

    private startPreparedSurfaceMotion() {
        if (this.surfaceMotionPhase !== "preparing") return;
        const surface = this.root.querySelector<HTMLElement>(".is-vault-surface-entering");
        if (!surface || surface === this.surfaceMotionElement) return;
        this.surfaceMotionElement = surface;
        if (this.surfaceReturn) {
            const tile = Array.from(surface.querySelectorAll<HTMLElement>("[data-vault-id]"))
                .find(candidate => candidate.dataset.vaultId === this.surfaceReturn!.vaultId);
            if (tile) {
                this.surfaceReturn.cameraStyle = this.getVaultCameraStyle(surface, tile);
                surface.style.cssText = this.surfaceReturn.cameraStyle;
            }
            surface.inert = true;
        }
        if (typeof window.requestAnimationFrame !== "function") {
            this.clearSurfaceMotion();
            return;
        }
        const key = this.surfaceMotionKey;
        // Roster fitting and the first style/layout pass happen before playback.
        // A stalled render must never spend the entrance's visible duration.
        this.surfaceMotionFrameId = window.requestAnimationFrame(() => {
            this.surfaceMotionFrameId = window.requestAnimationFrame(() => {
                this.surfaceMotionFrameId = null;
                if (key !== this.surfaceMotionKey || !surface.isConnected || this.surfaceMotionPhase !== "preparing") return;
                if (!SITE_TUNING.vaultBreakers.motion.enabled ||
                    document.documentElement.classList.contains("ziar-vault-motion-disabled") ||
                    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
                    this.clearSurfaceMotion();
                    return;
                }
                this.surfaceMotionStartedAt = performance.now();
                this.surfaceMotionPhase = "playing";
                surface.classList.remove("is-vault-surface-preparing");
                this.surfaceReturn?.overlay.classList.remove("is-vault-return-preparing");
                const pendingReturn = this.surfaceReturn;
                if (pendingReturn) {
                    this.surfaceMotionTimerId = window.setTimeout(() => {
                        if (this.surfaceReturn !== pendingReturn) return;
                        // Explicitly paused developer previews retain their clock.
                        if (this.isSurfaceMotionEnabled() && surface.getAnimations().some(animation =>
                            (animation as CSSAnimation).animationName === "vaultBoardReturn" && animation.playState === "paused")) return;
                        try {
                            if (localStorage.getItem("ziarcadia:vault-motion-debug") !== "0") {
                                const message = `phase=return-fallback vault=${pendingReturn.vaultId} expected_ms=${SITE_TUNING.vaultBreakers.motion.vaultReturnZoomMs} reduced_motion=${Boolean(this.reducedMotionQuery?.matches)}`;
                                console.info(`[Ziarcadia][VaultMotion] ${message}`);
                                appendCodexDebugLog("vault-breakers", "info", message);
                            }
                        } catch { /* Diagnostics cannot keep the board locked. */ }
                        this.clearSurfaceMotion();
                    }, SITE_TUNING.vaultBreakers.motion.vaultReturnZoomMs + 180);
                }
                this.root.classList.remove("is-vault-motion-preparing");
            });
        });
    }

    private readonly handleSurfaceMotionEnd = (event: AnimationEvent) => {
        if (["vaultSurfaceExit", "vaultEntryZoom"].includes(event.animationName) && event.target === this.surfaceExit?.element) {
            this.finishSurfaceExit();
            return;
        }
        if (!["vaultSurfaceSettle", "vaultBoardReturn"].includes(event.animationName) || event.target !== this.surfaceMotionElement) return;
        const preparationMs = this.surfaceMotionStartedAt - this.surfaceMotionRequestedAt;
        const playbackMs = performance.now() - this.surfaceMotionStartedAt;
        const expectedMs = event.animationName === "vaultBoardReturn" ? SITE_TUNING.vaultBreakers.motion.vaultReturnZoomMs : VAULT_SURFACE_SETTLE_MS;
        if (event.type === "animationend" && this.surfaceMotionPhase === "playing" &&
            (preparationMs >= SITE_TUNING.vaultBreakers.motion.slowSurfacePreparationMs || playbackMs > expectedMs + 120)) {
            try {
                if (localStorage.getItem("ziarcadia:vault-motion-debug") !== "0") {
                    const message = `phase=complete surface=${this.surfaceMotionKey} prepare_ms=${Math.round(preparationMs)} ` +
                        `playback_wall_ms=${Math.round(playbackMs)} expected_ms=${expectedMs} teams=${this.room?.vaultBreakers?.teams.length ?? 0}`;
                    console.info(`[Ziarcadia][VaultMotion] ${message}`);
                    appendCodexDebugLog("vault-breakers", "info", message);
                }
            } catch { /* Diagnostics must not interrupt playback cleanup. */ }
        }
        this.clearSurfaceMotion();
    }

    private getChoiceGridKey(room: RoomView, vault: VaultBreakerRoomView): string | null {
        const choices = vault.viewer?.choices ?? [];

        if (room.viewer.isHost || !vault.currentVaultId || choices.length === 0) {
            return null;
        }

        return [vault.roundNumber, vault.currentVaultId, vault.crackNumber ?? "none", choices.map((choice) => choice.id).join("|")].join(
            ":"
        );
    }

    private captureChoiceGridScrollRatio(): number | null {
        const grid = this.root.querySelector<HTMLElement>(".vault-choice-grid");

        if (!grid) {
            return null;
        }

        const maxScrollTop = Math.max(0, grid.scrollHeight - grid.clientHeight);

        if (maxScrollTop <= 0) {
            return 0;
        }

        return Math.min(1, Math.max(0, grid.scrollTop / maxScrollTop));
    }

    private restoreChoiceGridScrollRatio(scrollRatio: number | null) {
        if (scrollRatio === null) {
            return;
        }

        this.clearChoiceGridScrollRestoreTimer();

        let lastRestoredScrollTop: number | null = null;

        const restore = (onlyIfUntouched = false) => {
            const grid = this.root.querySelector<HTMLElement>(".vault-choice-grid");

            if (!grid) {
                return;
            }

            // The delayed pass corrects for late font/layout sizing, but a student's newer scroll must always win.
            if (onlyIfUntouched && lastRestoredScrollTop !== null && Math.abs(grid.scrollTop - lastRestoredScrollTop) > 1) {
                return;
            }

            const maxScrollTop = Math.max(0, grid.scrollHeight - grid.clientHeight);
            grid.scrollTop = Math.round(maxScrollTop * scrollRatio);
            lastRestoredScrollTop = grid.scrollTop;
        };

        restore();

        if (typeof window.requestAnimationFrame === "function") {
            window.requestAnimationFrame(() => restore(true));
        }

        this.choiceGridScrollRestoreTimer = window.setTimeout(() => {
            this.choiceGridScrollRestoreTimer = null;
            restore(true);
        }, VAULT_CHOICE_SCROLL_RESTORE_MS);
    }

    private getTimeBoundaryKey(room: RoomView, vault: VaultBreakerRoomView, now: number): string {
        const hostControlState =
            room.viewer.isHost && vault.currentVault && !vault.roundComplete && !isMassiveLootRevealActive(vault, now)
                ? vault.crackStartedAt !== null && now < vault.crackStartedAt
                    ? "pre-crack"
                    : vault.crackStartedAt !== null
                      ? "cracking"
                      : "ready"
                : "stable";

        return [
            isMassiveLootRevealActive(vault, now) ? "massive-loot" : "normal",
            getVaultFailedPauseCrackNumber(vault, now) ?? "no-failed-pause",
            getVaultPlayerStatusBoundaryKey(vault, now),
            hostControlState
        ].join(":");
    }

    private updateTimerRingElement(ring: HTMLElement | null, timer: VaultTimerDisplay) {
        if (!ring) {
            return;
        }

        for (const tone of VAULT_TIMER_TONES) {
            if (tone !== timer.tone && ring.classList.contains(tone)) {
                ring.classList.remove(tone);
            }
        }

        if (!ring.classList.contains(timer.tone)) {
            ring.classList.add(timer.tone);
        }
        const tickSegments = normalizeVaultTimerTickSegments(timer.tickSegments);
        const hasTicks = tickSegments > 1;
        const shouldPulse = isVaultTimerSecondPulseActive(timer);
        if (ring.classList.contains("has-ticks") !== hasTicks) {
            ring.classList.toggle("has-ticks", hasTicks);
        }
        ring.classList.toggle("is-second-pulse", shouldPulse);
        ring.style.setProperty("--vault-timer-fill", timer.fillPercent);
        if (tickSegments > 1) {
            ring.style.setProperty("--vault-timer-segments", String(tickSegments));
        } else {
            ring.style.removeProperty("--vault-timer-segments");
        }

        const label = ring.querySelector<HTMLElement>("span");
        if (label && label.textContent !== timer.time) {
            label.textContent = timer.time;
        }
    }

    private refreshActiveTimerDom(now: number): boolean {
        // Do not replace the outgoing question's timer/text with a new crack's data.
        if (this.surfaceExit) return false;
        const room = this.room;
        const vault = room?.vaultBreakers ?? null;

        if (!room || !vault) {
            this.clearSurfaceMotion();
            this.clearSurfaceExit();
            return false;
        }

        const hadScorePopups = this.teamScorePopupBySessionId.size > 0;
        const hasScorePopups = this.hasActiveTeamScorePopups();
        if (room.viewer.isHost && hadScorePopups && !hasScorePopups) {
            return false;
        }

        const nextTimeBoundaryKey = this.getTimeBoundaryKey(room, vault, now);
        if (this.renderedTimeBoundaryKey !== nextTimeBoundaryKey) {
            return false;
        }

        this.syncTranslationFade(room, vault, now);
        const timer = getVaultTimerDisplay(vault, now);
        const isResolving =
            vault.currentVault !== null && vault.crackStartedAt === null && vault.roundCompletedAt === null && vault.crackNumber !== null;

        this.root.classList.toggle("is-vault-resolving", isResolving);
        this.root.classList.toggle("is-vault-choice-hold", isVaultPlayerChoiceHold(room, vault, now));
        this.updateTimerRingElement(this.root.querySelector<HTMLElement>(".vault-player-timer .vault-timer-ring"), timer);
        this.updateTimerRingElement(this.root.querySelector<HTMLElement>(".vault-crack-timer.is-current .vault-timer-ring"), timer);

        const stage = this.root.querySelector<HTMLElement>(".vault-preview-stage");
        if (stage) {
            for (const tone of VAULT_TIMER_TONES) {
                if (tone !== timer.tone && stage.classList.contains(`is-${tone}`)) stage.classList.remove(`is-${tone}`);
            }
            if (!stage.classList.contains(`is-${timer.tone}`)) stage.classList.add(`is-${timer.tone}`);
        }

        return true;
    }

    private now(): number {
        return Date.now() + this.serverOffsetMs;
    }

    private syncGrowingStashIncrease(room: RoomView) {
        const vault = room.vaultBreakers;
        const points = vault ? getBoardGrowingStashBonusPoints(vault) : 0;
        if (!room.viewer.isHost || !vault || this.growingStashRoomCode !== room.roomCode ||
            points < this.lastGrowingStashBonusPoints) {
            // Joining an existing game establishes a baseline, not a new reward.
            this.growingStashRoomCode = room.viewer.isHost && vault ? room.roomCode : null;
            this.lastGrowingStashBonusPoints = points;
            this.pendingGrowingStashIncrease = 0;
            this.growingStashIncrease = null;
            return;
        }
        if (points > this.lastGrowingStashBonusPoints) {
            this.pendingGrowingStashIncrease += points - this.lastGrowingStashBonusPoints;
            this.growingStashIncrease = null;
        }
        this.lastGrowingStashBonusPoints = points;
    }

    private syncRenderTimer() {
        const vault = this.room?.vaultBreakers ?? null;
        const now = this.now();
        const hasScorePopups = this.hasActiveTeamScorePopups();
        const hasChooserSpotlight = this.hasActiveChooserSpotlight(now);
        const needsTimer = Boolean(
            vault?.roundActive ||
            (vault?.currentVault && vault.crackStartedAt === null && vault.roundCompletedAt === null) ||
            hasScorePopups ||
            hasChooserSpotlight ||
            this.growingStashIncrease
        );

        if (!needsTimer) {
            this.clearRenderTimer();
            return;
        }

        if (this.renderTimer !== null) {
            return;
        }

        this.renderTimer = window.setInterval(() => {
            const now = this.now();
            const room = this.room;
            const stashFinished = Boolean(this.growingStashIncrease &&
                now >= this.growingStashIncrease.startedAt + SITE_TUNING.vaultBreakers.motion.stashIncreaseMs);
            if (stashFinished) this.growingStashIncrease = null;
            const spotlightChanged = this.syncChooserSpotlight(room?.vaultBreakers ?? null, room?.viewer.isHost ?? false, now);
            this.syncAutoAdvanceCrack(room, now);
            this.syncAudio(room);
            if (stashFinished || spotlightChanged || !this.refreshActiveTimerDom(now)) {
                this.render();
            }
            this.syncRenderTimer();
        }, 250);
    }

    private clearRenderTimer() {
        if (this.renderTimer !== null) {
            window.clearInterval(this.renderTimer);
            this.renderTimer = null;
        }
    }

    private syncAudio(room: RoomView | null) {
        const vault = room?.vaultBreakers ?? null;

        if (!room || !vault) {
            this.audio.fadeOutQuestionTheme();
            this.lastQuestionAudioKey = "";
            this.lastMassiveLootAudioKey = "";
            this.lastDiamondFoundAudioKey = "";
            this.lastStashFoundAudioKey = "";
            this.lastCompletedAudioKey = "";
            this.lastFeedbackAudioKey = "";
            this.lastFailedCrackAudioKey = "";
            this.lastChooserSelectedAudioKey = "";
            this.lastFinalVaultAudioKey = "";
            return;
        }

        if (vault.roundActive || vault.currentVault) {
            void this.audio.prepare();
        }

        const now = this.now();
        const growingStashRevealActive = isGrowingStashRevealActive(vault, now);
        const massiveLootRevealActive = isMassiveLootRevealActive(vault, now);
        const specialRevealActive = growingStashRevealActive || massiveLootRevealActive;
        const questionTheme: VaultBreakersQuestionTheme =
            vault.finalVaultState === "active" || vault.finalVaultState === "complete" || normalizePointMultiplier(vault.pointMultiplier) > 1
                ? "massiveLoot"
                : "default";
        const diamondFoundAudioKey = room.viewer.isHost ? this.getDiamondSpotlightKey(vault, now) : "";
        if (diamondFoundAudioKey && diamondFoundAudioKey !== this.lastDiamondFoundAudioKey) {
            this.audio.playDiamondFound();
        }
        this.lastDiamondFoundAudioKey = diamondFoundAudioKey;

        // Announce the finale when the host arms the red board. Keep the key
        // through selection and results so room updates cannot replay the cue.
        const finalVaultAudioKey = room.viewer.isHost && vault.finalVaultState !== "idle" ? room.roomCode : "";
        if (finalVaultAudioKey && finalVaultAudioKey !== this.lastFinalVaultAudioKey && vault.finalVaultState !== "complete") {
            this.audio.playFinalVault();
        }
        this.lastFinalVaultAudioKey = finalVaultAudioKey;

        const chooserSelectedAudioKey =
            !room.viewer.isHost &&
            !room.viewer.isController &&
            vault.currentVault === null &&
            vault.chooserSessionId !== null &&
            vault.chooserSessionId === room.viewer.sessionId
                ? `${room.roomCode}:${vault.roundNumber}:${vault.chooserSessionId}`
                : "";

        if (chooserSelectedAudioKey && chooserSelectedAudioKey !== this.lastChooserSelectedAudioKey) {
            this.audio.playChooserSelected();
        }
        this.lastChooserSelectedAudioKey = chooserSelectedAudioKey;

        const questionAudioKey =
            vault.currentVaultId && vault.roundCompletedAt === null
                ? `${room.roomCode}:${vault.roundNumber}:${vault.currentVaultId}:${questionTheme}`
                : "";

        if (questionAudioKey) {
            const isNewQuestionAudio = questionAudioKey !== this.lastQuestionAudioKey;
            if (room.viewer.isHost) {
                if (isNewQuestionAudio && !specialRevealActive) {
                    this.audio.playHostVaultSelected();
                }
                if (isNewQuestionAudio || !this.audio.isQuestionThemeActive(questionTheme)) {
                    this.audio.playQuestionTheme(questionTheme);
                }
            }

            this.lastQuestionAudioKey = questionAudioKey;
        }

        const stashRevealBelongsToCurrentVault =
            vault.currentVaultId !== null &&
            vault.currentVaultId === vault.growingStashVaultId &&
            vault.growingStashRevealEndsAt !== null;
        const stashFoundAudioKey =
            room.viewer.isHost && vault.currentVaultId && growingStashRevealActive
                ? `${room.roomCode}:${vault.roundNumber}:${vault.currentVaultId}:${vault.growingStashRevealEndsAt}`
                : "";

        if (stashFoundAudioKey && stashFoundAudioKey !== this.lastStashFoundAudioKey) {
            this.audio.playStashFound();
        }
        this.lastStashFoundAudioKey = stashFoundAudioKey;

        const massiveLootAudioKey =
            room.viewer.isHost && vault.currentVaultId && massiveLootRevealActive && !stashRevealBelongsToCurrentVault
                ? `${room.roomCode}:${vault.roundNumber}:${vault.currentVaultId}:${vault.massiveLootRevealEndsAt}`
                : "";

        if (massiveLootAudioKey && massiveLootAudioKey !== this.lastMassiveLootAudioKey) {
            this.audio.playDailyDouble();
        }
        this.lastMassiveLootAudioKey = massiveLootAudioKey;

        const crackNumber = vault.crackNumber;
        const failedCrackAudioKey =
            vault.currentVaultId &&
            vault.roundCompletedAt === null &&
            crackNumber !== null &&
            vault.crackStartedAt !== null &&
            now < vault.crackStartedAt &&
            vault.teams.some((team) => team.submittedCracks.includes(crackNumber - 1)) &&
            !vault.teams.every((team) => team.solved)
                ? `${room.roomCode}:${vault.roundNumber}:${vault.currentVaultId}:${crackNumber}:pending`
                : vault.currentVaultId &&
                    vault.roundCompletedAt === null &&
                    crackNumber !== null &&
                    vault.crackStartedAt === null &&
                    vault.teams.some((team) => team.submittedCracks.includes(crackNumber)) &&
                    !vault.teams.every((team) => team.solved)
                  ? `${room.roomCode}:${vault.roundNumber}:${vault.currentVaultId}:${crackNumber}:resolved`
                  : "";

        if (failedCrackAudioKey && failedCrackAudioKey !== this.lastFailedCrackAudioKey) {
            if (room.viewer.isHost) {
                this.audio.playFailedToCrack();
            }

            this.lastFailedCrackAudioKey = failedCrackAudioKey;
        }

        const completedAudioKey = `${room.roomCode}:${vault.roundNumber}:${vault.roundCompletedAt ?? "open"}`;
        if (vault.roundCompletedAt !== null && completedAudioKey !== this.lastCompletedAudioKey) {
            if (room.viewer.isHost) {
                this.audio.fadeOutQuestionTheme();
                if (vault.teams.some((team) => team.solved)) {
                    this.audio.playVaultCracked();
                } else {
                    this.audio.playFailedToCrack();
                }
            }

            this.lastCompletedAudioKey = completedAudioKey;
        }

        const viewer = vault.viewer;
        const feedbackAudioKey = viewer
            ? [
                  room.roomCode,
                  vault.roundNumber,
                  vault.crackNumber ?? "none",
                  viewer.selectedChoiceId ?? "none",
                  viewer.submittedCracks.join("."),
                  viewer.lastFeedbackKind ?? "none",
                  viewer.lastFeedback ?? ""
              ].join(":")
            : "";

        if (viewer && feedbackAudioKey && feedbackAudioKey !== this.lastFeedbackAudioKey) {
            if (viewer.lastFeedbackKind === "correct") {
                this.audio.playVaultCracked();
            } else if (viewer.lastFeedbackKind === "wrong" || viewer.lastFeedback === "No code armed.") {
                this.audio.playFailedToCrack();
            } else if (viewer.lastFeedbackKind === "info" && viewer.lastFeedback === "Code armed.") {
                this.audio.playWordSelected();
            }

            this.lastFeedbackAudioKey = feedbackAudioKey;
        }
    }

    private pruneExpiredTeamScorePopups(now = Date.now()) {
        for (const [sessionId, popup] of this.teamScorePopupBySessionId.entries()) {
            if (popup.expiresAt <= now) {
                this.teamScorePopupBySessionId.delete(sessionId);
            }
        }
    }

    private hasActiveTeamScorePopups(now = Date.now()): boolean {
        this.pruneExpiredTeamScorePopups(now);
        return this.teamScorePopupBySessionId.size > 0;
    }

    private syncTeamScorePopups(vault: VaultBreakerRoomView | null, showPopups: boolean) {
        const now = Date.now();
        this.pruneExpiredTeamScorePopups(now);

        if (!vault) {
            this.teamScoreBySessionId.clear();
            this.teamScorePopupBySessionId.clear();
            return;
        }

        if (!showPopups) {
            this.teamScorePopupBySessionId.clear();
            this.teamScoreBySessionId = new Map(vault.teams.map((team) => [team.sessionId, team.score]));
            return;
        }

        for (const team of vault.teams) {
            const previousScore = this.teamScoreBySessionId.get(team.sessionId);

            if (previousScore === undefined || team.score <= previousScore) {
                continue;
            }

            const points = team.score - previousScore;
            const award = team.lastAward ?? null;
            const tone =
                award && !award.correct && normalizeAwardPoints(award.effortPoints) > 0 && normalizeAwardPoints(award.scoreDelta) === points
                    ? "effort"
                    : "crack";

            this.teamScorePopupBySessionId.set(team.sessionId, {
                points,
                tone,
                sequence: (this.teamScorePopupSequence += 1),
                startedAt: now,
                expiresAt: now + VAULT_TEAM_SCORE_POPUP_MS
            });
        }

        this.teamScoreBySessionId = new Map(vault.teams.map((team) => [team.sessionId, team.score]));
    }

    private renderTeamScorePopupMarkup(sessionId: string): string {
        const popup = this.teamScorePopupBySessionId.get(sessionId);

        if (!popup || popup.expiresAt <= Date.now()) {
            return "";
        }

        const sizeClass = popup.points >= 100 ? " is-big-award" : "";
        const toneClass = popup.tone === "effort" ? " is-effort" : " is-crack";
        const elapsedMs = Math.max(0, Math.min(VAULT_TEAM_SCORE_POPUP_MS, Date.now() - popup.startedAt));

        return `<span class="vault-score-popup${sizeClass}${toneClass}" style="--vault-score-popup-delay: -${elapsedMs}ms;" data-score-popup-sequence="${escapeHtml(
            String(popup.sequence)
        )}" aria-hidden="true">${escapeHtml(formatMoneyDelta(popup.points))}</span>`;
    }

    private render() {
        const previousChoiceGridKey = this.renderedChoiceGridKey;
        const choiceGridScrollRatio = this.captureChoiceGridScrollRatio();
        const room = this.room;
        const vault = room?.vaultBreakers ?? null;

        if (!room || !vault) {
            this.clearSurfaceMotion();
            this.clearSurfaceExit();
            this.capyRunner.sync({ enabled: false, chooserKey: null, hue: null });
            if (room) this.answerCover.sync(room);
            this.root.innerHTML = "";
            this.renderedChoiceGridKey = null;
            this.renderedTimeBoundaryKey = null;
            this.clearChoiceGridScrollRestoreTimer();
            return;
        }

        const now = this.now();
        // Public answer revelation ends private concealment immediately, even while
        // the old answer panel is completing its visual exit.
        this.answerCover.sync(room);
        if (this.holdForSurfaceExit(room, vault, now)) return;
        this.syncAwardAnimation(vault, now);
        this.syncSurfaceMotion(room, vault, now);
        const isResolving =
            vault.currentVault !== null && vault.crackStartedAt === null && vault.roundCompletedAt === null && vault.crackNumber !== null;
        const nextChoiceGridKey = this.getChoiceGridKey(room, vault);
        this.root.classList.toggle("is-vault-resolving", isResolving);
        this.root.classList.toggle("is-vault-choice-hold", isVaultPlayerChoiceHold(room, vault, now));
        const markup = room.viewer.isHost ? this.renderHost(room, vault, now) : this.renderPlayer(room, vault, now);
        updateVaultBreakersDom(this.root, markup);
        this.categoryZoom.sync();
        this.answerCover.sync(room);
        this.syncTranslationFade(room, vault, now);
        this.mountPlayerWaitingCapy(room);
        this.capyRunner.sync({
            // The runner explains the live board selector, so board announcements must not make it blink out.
            announcementActive: this.hasActiveChooserSpotlight(now),
            enabled: room.viewer.isHost && vault.currentVault === null && !vault.roundComplete,
            chooserKey: vault.chooserSessionId,
            hue: vault.chooserAvatarHue
        });
        this.renderedChoiceGridKey = nextChoiceGridKey;
        this.renderedTimeBoundaryKey = this.getTimeBoundaryKey(room, vault, now);
        const nextRosterLayoutKey = `${this.surfaceMotionKey}:${JSON.stringify(vault.teams)}`;
        this.scheduleRosterLayoutSync(nextRosterLayoutKey !== this.rosterLayoutKey);
        this.rosterLayoutKey = nextRosterLayoutKey;

        if (previousChoiceGridKey && previousChoiceGridKey === nextChoiceGridKey) {
            this.restoreChoiceGridScrollRatio(choiceGridScrollRatio);
        } else {
            this.clearChoiceGridScrollRestoreTimer();
        }

        this.options.onAfterRender?.();
        this.startPreparedSurfaceMotion();
    }

    private mountPlayerWaitingCapy(room: RoomView) {
        const slot = this.root.querySelector<HTMLElement>("[data-vault-player-waiting-capy]");

        if (!slot || room.viewer.isHost) {
            return;
        }

        if (slot.firstElementChild) {
            setCapyShieldHue(slot.firstElementChild as HTMLElement, room.viewer.avatarHue);
            return;
        }

        slot.append(
            createVaultCapyShieldElement({
                className: "vault-player-waiting-capy",
                hue: room.viewer.avatarHue
            })
        );
    }

    private syncAwardAnimation(vault: VaultBreakerRoomView, now: number) {
        const award = vault.viewer?.lastAward ?? null;
        const awardKey =
            award && award.scoreDelta > 0
                ? [
                      vault.roundNumber,
                      award.crackNumber,
                      award.correct ? "correct" : "effort",
                      award.scoreDelta,
                      award.basePoints,
                      award.earlyBonusPoints,
                      award.comebackBonusPoints,
                      award.effortPoints,
                      normalizePointMultiplier(award.pointMultiplier),
                      normalizeAwardPoints(award.diamondBonusPoints),
                      award.lockRank ?? 0,
                      award.lockCount
                  ].join(":")
                : "";

        if (award && awardKey && awardKey !== this.lastAwardAnimationKey) {
            this.lastAwardAnimationKey = awardKey;
            this.activeAwardAnimation = {
                key: awardKey,
                startedAt: now,
                award
            };
        }

        if (this.activeAwardAnimation && now - this.activeAwardAnimation.startedAt > 2600) {
            this.activeAwardAnimation = null;
        }
    }

    private renderHost(room: RoomView, vault: VaultBreakerRoomView, now: number): string {
        const current = vault.currentVault;
        const selectedClass = current ? " has-selected-vault" : "";
        const surfaceMotionClass = this.getSurfaceMotionClass();

        return `
            <section class="vault-shell vault-host-shell${selectedClass}">
                <div class="vault-board-card vault-stage-card">
                    <div data-vault-surface-key="${escapeHtml(this.surfaceMotionKey ?? "")}" class="vault-surface-content${surfaceMotionClass}"${this.surfaceReturn ? ` inert style="${escapeHtml(this.surfaceReturn.cameraStyle)}"` : ""}>
                        ${current ? this.renderHostVaultStage(vault, now) : this.renderBoard(vault, now)}
                    </div>
                </div>
                <aside class="vault-host-panel">
                    ${this.renderHostUtilityCard(room, vault)}
                    ${this.renderHostRosterCard(room, vault, now)}
                </aside>
            </section>
        `;
    }

    private renderHostUtilityCard(room: RoomView, vault: VaultBreakerRoomView): string {
        const canToggleFinalVault =
            !isAwaitingFirstVaultRoll(vault) &&
            (vault.finalVaultState === "idle" || vault.finalVaultState === "armed") &&
            vault.remainingVaultCount > 0 &&
            vault.currentVault === null;
        const finalVaultArmed = vault.finalVaultState === "armed";
        const finalVaultAction = canToggleFinalVault
            ? `<button class="secondary-button vault-final-button${finalVaultArmed ? " is-active" : ""}" type="button" data-vault-toggle-final aria-pressed="${
                  finalVaultArmed ? "true" : "false"
              }">${escapeHtml(
                  t("vaultFinalVault")
              )}</button>`
            : "";

        return `
            <section class="vault-host-join-card">
                ${renderActiveHostJoinUtility({
                    roomCode: room.roomCode,
                    rootClassName: "vault-host-utility-strip",
                    actionsClassName: "vault-host-utility-actions",
                    joinPanelClassName: "vault-host-join-panel",
                    qrButtonClassName: "vault-room-qr-button",
                    qrFrameClassName: "vault-room-qr-frame",
                    controllerButtonClassName: "vault-host-controller-button",
                    showControllerButton: false,
                    extraActionsMarkup: finalVaultAction
                })}
            </section>
        `;
    }

    private renderHostRosterCard(room: RoomView, vault: VaultBreakerRoomView, now: number): string {
        const showSetupToolbar = vault.currentVault === null && !isVaultBankHeisted(vault);
        const awaitingFirstRoll = isAwaitingFirstVaultRoll(vault);
        const chooserName = vault.chooserName?.trim() ?? "";
        const hasChooser = chooserName.length > 0;
        const chooserLabel = hasChooser ? chooserName : t("noStudentsYet");
        const chooserTitle = hasChooser ? `${t("vaultChooser")}: ${chooserName}` : t("noStudentsYet");
        const finalVaultArmed = vault.finalVaultState === "armed";
        const crackDurationSeconds = finalVaultArmed
            ? vault.finalVaultCrackDurationSeconds
            : clampVaultBreakerCrackDurationSeconds(vault.crackDurationSeconds);
        const crackTimerLabel = t("vaultCrackTimer");
        const durationTitle = t("vaultCrackTimerSeconds", {
            seconds: String(crackDurationSeconds)
        });
        const canUnlockNextValue =
            showSetupToolbar && !finalVaultArmed && vault.unlockedValueIndex < getMaxValueIndex(vault.tiles);
        const unlockNextValueButtonMarkup = canUnlockNextValue
            ? `
                <button
                    class="secondary-button vault-unlock-value-button vault-icon-button"
                    type="button"
                    data-vault-unlock-next-value
                    aria-label="${escapeHtml(t("unlockNextVaultValue"))}"
                    title="${escapeHtml(t("unlockNextVaultValue"))}"
                >${renderZiarcadiaIcon("unlock", { className: "vault-unlock-value-icon" })}</button>
            `
            : "";
        const crackDurationEditorOpen = showSetupToolbar && !finalVaultArmed && this.crackDurationEditorOpen;
        const crackDurationEditorOpenClass = crackDurationEditorOpen ? " is-open" : "";
        const setupToolbarMarkup = showSetupToolbar
            ? `
                <div class="vault-roster-toolbar">
                    <div class="vault-roster-primary-row">
                        ${awaitingFirstRoll ? "" : `
                        <button
                            class="vault-chooser-chip${hasChooser ? " is-ready" : ""}"
                            type="button"
                            data-vault-show-chooser
                            title="${escapeHtml(chooserTitle)}"
                            aria-label="${escapeHtml(chooserTitle)}"
                            ${hasChooser ? "" : "disabled"}
                        >
                            <span class="vault-chooser-dot" aria-hidden="true"></span>
                            <span class="vault-chooser-label">${escapeHtml(chooserLabel)}</span>
                        </button>
                        <button
                            class="secondary-button vault-roll-button vault-icon-button"
                            type="button"
                            data-vault-roll
                            aria-label="${escapeHtml(t("rollChooser"))}"
                            title="${escapeHtml(t("rollChooser"))}"
                        >${renderZiarcadiaIcon("dice", { className: "vault-roll-icon" })}</button>
                        `}
                        ${unlockNextValueButtonMarkup}
                        <div class="vault-crack-duration-control${crackDurationEditorOpenClass}" role="group" aria-label="${escapeHtml(
                            crackTimerLabel
                        )}">
                            <button
                                class="vault-crack-duration-trigger"
                                type="button"
                                data-vault-crack-duration-toggle
                                aria-label="${escapeHtml(durationTitle)}"
                                aria-expanded="${crackDurationEditorOpen ? "true" : "false"}"
                                title="${escapeHtml(durationTitle)}"
                                ${finalVaultArmed ? "disabled" : ""}
                            >
                                ${renderZiarcadiaIcon("lock", { className: "vault-crack-duration-icon" })}
                                <strong>${escapeHtml(String(crackDurationSeconds))}</strong>
                                <span aria-hidden="true">s</span>
                            </button>
                            <div
                                class="vault-crack-duration-editor${crackDurationEditorOpenClass}"
                                data-vault-crack-duration-editor
                                role="group"
                                aria-label="${escapeHtml(crackTimerLabel)}"
                            >
                                <button
                                    class="vault-crack-duration-step"
                                    type="button"
                                    data-vault-crack-duration-step="-1"
                                    aria-label="${escapeHtml(t("decreaseVaultCrackTimer"))}"
                                    title="${escapeHtml(t("decreaseVaultCrackTimer"))}"
                                >-</button>
                                <span class="vault-crack-duration-input-wrap">
                                    <input
                                        class="vault-crack-duration-input"
                                        type="number"
                                        inputmode="numeric"
                                        min="${VAULT_BREAKER_MIN_CRACK_DURATION_SECONDS}"
                                        max="${VAULT_BREAKER_MAX_CRACK_DURATION_SECONDS}"
                                        step="1"
                                        value="${crackDurationSeconds}"
                                        data-vault-crack-duration-input
                                        aria-label="${escapeHtml(crackTimerLabel)}"
                                        title="${escapeHtml(durationTitle)}"
                                    />
                                    <span aria-hidden="true">s</span>
                                </span>
                                <button
                                    class="vault-crack-duration-step"
                                    type="button"
                                    data-vault-crack-duration-step="1"
                                    aria-label="${escapeHtml(t("increaseVaultCrackTimer"))}"
                                    title="${escapeHtml(t("increaseVaultCrackTimer"))}"
                                >+</button>
                            </div>
                        </div>
                    </div>
                </div>
            `
            : "";

        return `
            <section class="roster-card vault-score-card vault-roster-card${showSetupToolbar ? "" : " is-question-view"}">
                <div class="roster-head vault-roster-head">
                    ${renderPlayerHistoryHeaderMenu(room, String(vault.teams.length), {
                        open: this.playerHistoryMenuOpen
                    })}
                </div>
                ${setupToolbarMarkup}
                <div class="roster-body">
                    ${this.renderTeamList(vault, now)}
                </div>
            </section>
        `;
    }

    private renderHostVaultStage(vault: VaultBreakerRoomView, now: number): string {
        const current = vault.currentVault;

        if (!current) {
            return this.renderBoard(vault, now);
        }

        const timer = getVaultTimerDisplay(vault, now);
        const allSolved = vault.teams.length > 0 && vault.teams.every((team) => team.solved);
        const stageClasses = [
            "vault-preview-stage",
            `is-${timer.tone}`,
            vault.crackNumber ? `is-crack-${vault.crackNumber}` : "",
            vault.roundComplete ? "is-round-complete" : "",
            vault.finalVaultState === "active" || vault.finalVaultState === "complete" ? "is-final-vault" : "",
            allSolved ? "is-success" : ""
        ]
            .filter(Boolean)
            .join(" ");
        const vaultValueMarkup = `
            <div class="vault-preview-value${vault.roundComplete ? " vault-result-value" : ""}">
                <strong aria-label="${escapeHtml(formatVaultDisplayValue(current))}">${renderVaultDisplayValue(current, {
                    showMassiveLootLabel: true,
                    banknote: vault.roundComplete
                })}</strong>
            </div>
        `;
        const promptFitClass = getVaultPromptFitClass(current.prompt);
        const promptClassName = ["vault-question-text", "vault-preview-question", promptFitClass].filter(Boolean).join(" ");
        const currentInfo = getVaultBreakerSupplementalInfo(current.korean);

        return `
            <section class="${stageClasses}" role="group" aria-label="${escapeHtml(t("vaultBreakers"))}">
                ${this.renderCategoryStrip(vault)}
                ${this.renderVaultCrackTimerStrip(vault, now)}
                <div class="vault-preview-main">
                    ${
                        isGrowingStashRevealActive(vault, now)
                            ? renderVaultGrowingStashReveal(current.growingStashBonusPoints)
                            : isMassiveLootRevealActive(vault, now)
                              ? renderVaultMassiveLootReveal(current.pointMultiplier)
                              : `
                                <div class="${vault.roundComplete ? "vault-current-result" : "vault-current-prompt"} vault-preview-content">
                                    <p class="${promptClassName}">${escapeHtml(current.prompt)}</p>
                                    ${
                                        vault.roundComplete
                                            ? `<div class="vault-answer-reveal vault-preview-answer">
                                                  <div class="vault-answer-copy">
                                                      <strong class="vault-answer-term">${escapeHtml(
                                                          formatVaultBreakerDisplayTerm(current.answer)
                                                      )}</strong>
                                                      ${currentInfo ? `<em class="vault-answer-korean">${escapeHtml(currentInfo)}</em>` : ""}
                                                  </div>
                                                  ${vaultValueMarkup}
                                                  <span class="vault-banknote-fold" aria-hidden="true"></span>
                                              </div>`
                                            : ""
                                    }
                                </div>
                            `
                    }
                    ${vault.roundComplete ? "" : vaultValueMarkup}
                </div>
                <div class="vault-preview-control-dock${vault.roundComplete ? " is-complete" : ""}">
                    ${this.renderHostRoundControls(vault, now)}
                </div>
            </section>
        `;
    }

    private renderCategoryStrip(vault: VaultBreakerRoomView): string {
        const category = vault.categories.find((candidate) => candidate.index === vault.currentVault?.categoryIndex);
        const diamond = normalizeDiamondBonusPoints(vault.currentVault?.diamondBonusPoints) > 0;
        return category ? `<div class="vault-question-category${diamond ? " has-diamond" : ""}">${escapeHtml(formatVaultBreakerDisplayTerm(category.title))}</div>` : "";
    }

    private getCrackHandoffKey(vault: VaultBreakerRoomView, now: number): string {
        if (!vault.currentVaultId || vault.crackNumber === null || vault.roundComplete ||
            isGrowingStashRevealActive(vault, now) || isMassiveLootRevealActive(vault, now)) return "";
        // Exclude countdown timestamps so arming and passive snapshots retain this one-time cue.
        return `${vault.roundNumber}:${vault.currentVaultId}:c${vault.crackNumber}`;
    }

    private renderVaultCrackTimerStrip(vault: VaultBreakerRoomView, now: number): string {
        const activeTimer = getVaultTimerDisplay(vault, now);
        const currentCrack = vault.crackNumber ?? 1;
        const crackCount = vault.finalVaultState === "active" || vault.finalVaultState === "complete" ? 1 : VAULT_BREAKER_CRACK_COUNT;

        return `
            <div class="vault-crack-timer-strip" aria-label="${escapeHtml(
                t("vaultCrackProgress", {
                    count: String(Math.max(0, Math.min(crackCount, currentCrack - (vault.roundComplete ? 0 : 1)))),
                    total: String(crackCount)
                })
            )}">
                ${Array.from({ length: crackCount }, (_, index) => {
                    const crack = index + 1;
                    const status = getVaultCrackStatus(vault, crack);
                    const isCurrent = status === "active";
                    const isCracked = status === "cracked";
                    const isFailed = status === "failed";
                    const timer: VaultTimerDisplay = isCurrent
                        ? activeTimer
                        : {
                              stage: `C${crack}`,
                              time: isCracked ? "🔑" : isFailed ? "X" : "🔒",
                              fillPercent: isCracked || isFailed ? "100%" : "0%",
                              tone: isCracked ? "success" : isFailed ? "failed" : "idle",
                              tickSegments: getVaultTimerTickSegments(vault, crack as VaultBreakerCrackNumber)
                          };
                    const classes = [
                        "vault-crack-timer",
                        `is-crack-${crack}`,
                        isCurrent ? "is-current" : "",
                        isCracked ? "is-done" : "",
                        isFailed ? "is-failed" : "",
                        status === "locked" ? "is-pending" : ""
                    ]
                        .filter(Boolean)
                        .join(" ");

                    return `
                        <div class="${classes}">
                            <span>${escapeHtml(`C${crack}`)}</span>
                            ${renderVaultTimerRing(timer, "", isCurrent ? this.getCrackHandoffKey(vault, now) : "")}
                        </div>
                    `;
                }).join("")}
            </div>
        `;
    }

    private renderBoard(vault: VaultBreakerRoomView, now: number): string {
        const categories = [...vault.categories].sort((left, right) => left.index - right.index);
        const tilesBySlot = getTileBySlot(vault.tiles);
        const maxValueIndex = getMaxValueIndex(vault.tiles);
        const awaitingFirstRoll = isAwaitingFirstVaultRoll(vault);
        const boardLocked = awaitingFirstRoll || vault.currentVault !== null && !vault.roundComplete;
        const canRoll = vault.teams.some(team => team.connected && !team.isAway);
        const firstRollMarkup = awaitingFirstRoll ? `
            <div class="vault-first-roll">
                <button class="vault-chooser-panel vault-first-roll-button" type="button" data-vault-roll
                    ${canRoll ? "" : "disabled"}>
                    ${renderVaultChooserDial()}
                    <span class="vault-chooser-spotlight-copy"><strong>${escapeHtml(t("rollFirstStudent"))}</strong></span>
                </button>
            </div>` : "";
        const finalVaultArmed = vault.finalVaultState === "armed";
        // The stash is awarded while a question is open. Show its board-wide
        // increase once the board is visible, rather than spending the cue offscreen.
        if (this.pendingGrowingStashIncrease > 0) {
            this.growingStashIncrease = {
                points: this.pendingGrowingStashIncrease,
                startedAt: now + (this.isSurfaceMotionEnabled() ? SITE_TUNING.vaultBreakers.motion.surfaceSettleMs : 0),
                sequence: ++this.growingStashSequence
            };
            this.pendingGrowingStashIncrease = 0;
            this.syncRenderTimer();
        }
        const stashIncrease = this.growingStashIncrease;
        const stashDelay = stashIncrease ? Math.round(stashIncrease.startedAt - now) : 0;
        const diamondGleamElapsedMs = this.diamondColumnGleam
            ? Math.max(0, Math.round(now - this.diamondColumnGleam.startedAt))
            : Number.POSITIVE_INFINITY;
        const getDiamondGleamAttributes = (categoryIndex: number, rowIndex: number) => {
            if (
                !this.diamondColumnGleam ||
                this.diamondColumnGleam.categoryIndex !== categoryIndex ||
                diamondGleamElapsedMs > VAULT_DIAMOND_COLUMN_GLEAM_MS
            ) {
                return { className: "", style: "" };
            }

            const delayMs = rowIndex * 90 - diamondGleamElapsedMs;
            return {
                className: " is-diamond-arriving",
                style: ` style="--vault-diamond-column-gleam-delay: ${delayMs}ms;"`
            };
        };

        return `
            <div class="vault-board-frame${
                finalVaultArmed ? " is-final-vault" : ""
            }${awaitingFirstRoll ? " is-awaiting-first-roll" : ""}">
                ${
                    finalVaultArmed
                        ? `<div class="vault-final-banner" role="status">
                              <div class="vault-final-banner-copy">
                                  <span>${escapeHtml(t("vaultOneLastHeist"))}</span>
                                  <small>${escapeHtml(t("vaultFinalOneCrackExplanation"))}</small>
                              </div>
                              <strong>${escapeHtml(formatMoney(vault.finalVaultPrize))}</strong>
                              <em>${escapeHtml(
                                  t("vaultFinalDetails", {
                                      seconds: String(vault.finalVaultCrackDurationSeconds)
                                  })
                              )}</em>
                          </div>`
                        : ""
                }
                <div class="vault-board-grid" style="--vault-board-columns: ${categories.length}; --vault-board-rows: ${
                    maxValueIndex + 1
                }">
                    ${categories
                        .map((category) => {
                            const diamondBonusPoints = vault.tiles.reduce(
                                (highest, tile) =>
                                    tile.categoryIndex === category.index
                                        ? Math.max(highest, normalizeDiamondBonusPoints(tile.diamondBonusPoints))
                                        : highest,
                                0
                            );
                            const diamondSummary = renderVaultDiamondWorthChip(
                                {
                                    value: 0,
                                    pointMultiplier: 1,
                                    diamondBonusPoints,
                                    growingStashBonusPoints: 0
                                },
                                true
                            );
                            const diamondGleam = diamondSummary ? getDiamondGleamAttributes(category.index, 0) : null;

                            return `
                                <div class="vault-category-cell${diamondSummary ? " has-diamond" : ""}${
                                    diamondGleam?.className ?? ""
                                }"${diamondGleam?.style ?? ""} data-vault-category="${category.index}" tabindex="0" role="button" aria-expanded="false" aria-label="${escapeHtml(formatVaultBreakerDisplayTerm(category.title))}">
                                    <span class="vault-category-title">${escapeHtml(
                                        formatVaultBreakerDisplayTerm(category.title)
                                    )}</span>
                                    ${diamondSummary}
                                </div>
                            `;
                        })
                        .join("")}
                    ${Array.from({ length: maxValueIndex + 1 }, (_, valueIndex) =>
                        categories
                            .map((category) => {
                                const tile = tilesBySlot.get(`${category.index}:${valueIndex}`);

                                if (!tile) {
                                    return `<div class="vault-tile-cell is-empty"></div>`;
                                }

                                const disabled = tile.locked || tile.cleared || boardLocked;
                                const displayValue = renderVaultDisplayValue(tile, { compact: true, showBonusChips: false });
                                const previousStashTile = stashIncrease && !tile.cleared
                                    ? { ...tile, growingStashBonusPoints: Math.max(0,
                                        normalizeGrowingStashBonusPoints(tile.growingStashBonusPoints) - stashIncrease.points) }
                                    : null;
                                const increasePoints = previousStashTile
                                    ? getVaultDisplayValue(tile) - getVaultDisplayValue(previousStashTile) : 0;
                                const stashAttributes = increasePoints > 0 && stashIncrease
                                    ? ` data-vault-stash-value="${stashIncrease.sequence}" style="--vault-stash-increase-delay: ${stashDelay}ms;"`
                                    : "";
                                const stashEffect = increasePoints > 0 && stashIncrease
                                    ? `<span class="vault-stash-increase" data-vault-stash-effect="${stashIncrease.sequence}"
                                          style="--vault-stash-increase-delay: ${stashDelay}ms;" aria-hidden="true">
                                           <span class="vault-stash-wash"></span>
                                           <span class="vault-stash-delta">${escapeHtml(formatMoneyDelta(increasePoints))}</span>
                                       </span>` : "";
                                const accessibleValue = formatVaultDisplayValue(tile);
                                const tileState = tile.cleared ? "cleared" : tile.locked ? "locked" : "available";
                                const diamondGleam =
                                    normalizeDiamondBonusPoints(tile.diamondBonusPoints) > 0
                                        ? getDiamondGleamAttributes(category.index, valueIndex + 1)
                                        : null;
                                const tileContent = tile.cleared
                                    ? `
                                        <span class="vault-tile-cleared-mark" aria-hidden="true"></span>
                                        <span class="visually-hidden">${escapeHtml(t("vaultDone"))}</span>
                                    `
                                    : `
                                        <span class="vault-tile-value"${stashAttributes}>${stashEffect
                                            ? `<span class="vault-stash-next">${displayValue}</span><span class="vault-stash-previous" aria-hidden="true">${renderVaultDisplayValue(previousStashTile!, { compact: true, showBonusChips: false })}</span>`
                                            : displayValue}</span>
                                        ${stashEffect}
                                        ${renderVaultMassiveLootBadge(tile.pointMultiplier)}
                                    `;
                                const classes = [
                                    "vault-tile-button",
                                    finalVaultArmed ? "is-final-vault" : "",
                                    finalVaultArmed && !tile.locked && !tile.cleared ? "is-final-eligible" : "",
                                    tile.locked ? "is-locked" : "",
                                    tile.cleared ? "is-cleared" : "",
                                    stashEffect ? "is-stash-increasing" : "",
                                    normalizeDiamondBonusPoints(tile.diamondBonusPoints) > 0 ? "is-diamond" : "",
                                    diamondGleam?.className.trim() ?? "",
                                    vault.currentVaultId === tile.id ? "is-current" : ""
                                ]
                                    .filter(Boolean)
                                    .join(" ");

                                return `
                                    <button
                                        class="${classes}"
                                        type="button"
                                        data-vault-id="${escapeHtml(tile.id)}"
                                        data-vault-state="${escapeHtml(tileState)}"
                                        aria-label="${escapeHtml(tile.cleared ? `${t("vaultDone")} ${accessibleValue}` : accessibleValue)}"
                                        ${diamondGleam?.style ?? ""}
                                        ${disabled ? "disabled" : ""}
                                    >
                                        ${tileContent}
                                    </button>
                                `;
                            })
                            .join("")
                    ).join("")}
                </div>
                ${stashIncrease ? `<span class="visually-hidden" role="status">${escapeHtml(
                    t("vaultGrowingStashAllVaults", { amount: `+${formatMoney(stashIncrease.points)}` })
                )}</span>` : ""}
                ${this.renderChooserSpotlight(now)}
                ${firstRollMarkup}
                ${isVaultBankHeisted(vault) ? this.renderBankHeistedCallout('overlay') : ''}
            </div>
        `;
	}

    private renderChooserSpotlight(now: number): string {
        const spotlight = this.chooserSpotlight;

        if (!spotlight || !this.hasActiveChooserSpotlight(now)) {
            return "";
        }

        const elapsedMs = Math.max(0, Math.round(now - spotlight.startedAt));
        const holdMs = getVaultChooserSpotlightHoldMs(spotlight.kind);
        const fadeOutMs = getVaultChooserSpotlightFadeMs(spotlight.kind);
        const fadeInDelayMs = VAULT_CHOOSER_SPOTLIGHT_DELAY_MS - elapsedMs;
        const fadeOutDelayMs = VAULT_CHOOSER_SPOTLIGHT_DELAY_MS + holdMs - elapsedMs;
        const totalMs = getVaultChooserSpotlightTotalMs(spotlight.kind);
        const dismissElapsedMs =
            spotlight.dismissStartedAt === null ? 0 : Math.max(0, Math.round(now - spotlight.dismissStartedAt));
        const dismissClass = spotlight.dismissStartedAt === null ? "" : " is-dismissing";
        const chooserLine = spotlight.kind === "chooser" && spotlight.name
            ? `
                        <span>${escapeHtml(t("vaultChose"))}</span>
                        <strong>${escapeHtml(spotlight.name)}</strong>
                    `
            : "";
        // Diamond and chooser each get a clear turn; combining them made the smaller chooser easy to miss.
        const diamondLine =
            spotlight.kind === "diamond" && spotlight.diamondCategoryTitle && spotlight.diamondAwardedPoints > 0
                ? `<span class="vault-chooser-diamond-pop">
                    <span class="vault-chooser-diamond-visual" aria-hidden="true">
                        ${renderVaultDiamondGem()}
                    </span>
                    <span class="vault-chooser-diamond-copy">
                        <strong class="vault-chooser-diamond-title">${escapeHtml(t("vaultDiamondFoundLabel"))}</strong>
                        <span class="vault-chooser-diamond-bonus">
                            <span class="vault-chooser-diamond-bonus-lead">
                                ${escapeHtml(t("vaultDiamondEveryVaultPrefix"))}
                                <span class="vault-chooser-diamond-category">${escapeHtml(spotlight.diamondCategoryTitle)}</span>
                            </span>
                            <span class="vault-chooser-diamond-bonus-result">${escapeHtml(
                                t("vaultDiamondEveryVaultSuffix")
                            )}</span>
                            <span class="vault-chooser-diamond-bonus-amount">+${escapeHtml(
                                formatMoney(
                                    spotlight.diamondTotalPoints > 0
                                        ? spotlight.diamondTotalPoints
                                        : spotlight.diamondAwardedPoints
                                )
                            )}!</span>
                        </span>
                    </span>
                </span>`
                : "";

        return `
            <div
                class="vault-chooser-spotlight${dismissClass}"
                data-vault-spotlight-kind="${spotlight.kind}"
                data-vault-spotlight-key="${escapeHtml(spotlight.key)}"
                style="--vault-chooser-spotlight-in-delay: ${fadeInDelayMs}ms; --vault-chooser-spotlight-out-delay: ${
                    fadeOutDelayMs
                }ms; --vault-chooser-spotlight-fade-in: ${VAULT_CHOOSER_SPOTLIGHT_FADE_IN_MS}ms; --vault-chooser-spotlight-fade-out: ${
                    fadeOutMs
                }ms; --vault-chooser-spotlight-total: ${totalMs}ms; --vault-chooser-spotlight-dismiss-delay: -${
                    dismissElapsedMs
                }ms; --vault-chooser-spotlight-dismiss-opacity: ${spotlight.dismissFromOpacity}; --vault-chooser-spotlight-dismiss-ms: ${
                    VAULT_CHOOSER_SPOTLIGHT_DISMISS_MS
                }ms;"
                role="status"
                aria-live="polite"
            >
                <button
                    class="vault-chooser-panel vault-chooser-spotlight-panel${diamondLine ? " has-diamond" : ""}"
                    type="button"
                    data-vault-dismiss-spotlight
                    title="${escapeHtml(t("close"))}"
                >
                    ${
                        spotlight.kind === "chooser" && spotlight.name
                            ? renderVaultChooserDial()
                            : ""
                    }
                    <span class="vault-chooser-spotlight-copy${spotlight.kind === "diamond" ? " is-diamond-only" : ""}">
                        ${diamondLine}
                        ${chooserLine}
                    </span>
                </button>
            </div>
        `;
	}

	private renderBankHeistedCallout(variant: 'dock' | 'overlay' = 'dock'): string {
		return `
            <div class="vault-bank-heisted-callout is-${variant}" role="group" aria-label="${escapeHtml(t('vaultBankHeistedTitle'))}">
                <span class="vault-bank-heisted-mark" aria-hidden="true">
                    <span></span><span></span><span></span>
                </span>
                <strong>${escapeHtml(t('vaultBankHeistedTitle'))}</strong>
                <button class="primary-button vault-results-button" type="button" data-end-game>${escapeHtml(t('vaultShowResults'))}</button>
            </div>
        `;
	}

	private renderHostRoundControls(vault: VaultBreakerRoomView, now: number): string {
		if (!vault.currentVault) {
			if (isVaultBankHeisted(vault)) {
				return `
                    <div class="vault-control-row is-bank-heisted">
                        ${this.renderBankHeistedCallout()}
                    </div>
                `;
			}

			return `
                <div class="vault-control-row">
                    <button class="secondary-button" type="button" data-vault-roll>${escapeHtml(t("rollChooser"))}</button>
                </div>
            `;
        }

		if (vault.roundComplete) {
			if (vault.finalVaultState === "complete") {
				return `
                    <div class="vault-control-row is-final-complete">
                        <button class="primary-button vault-results-button" type="button" data-end-game>${escapeHtml(
                            t("vaultShowResults")
                        )}</button>
                    </div>
                `;
			}

			const finalVaultButton =
				vault.finalVaultState === "idle" &&
				vault.remainingVaultCount > 0 &&
				vault.tiles.some(
					(tile) =>
						tile.value >= VAULT_BREAKER_LAST_QUESTION_MIN_VALUE &&
						tile.valueIndex <= vault.unlockedValueIndex
				)
					? `<button class="secondary-button vault-final-button vault-last-question-button" type="button" data-vault-toggle-final>${escapeHtml(
					      t("vaultLastQuestion")
				      )}</button>`
					: "";

			return `
                <div class="vault-control-row">
                    ${finalVaultButton}
                    <button class="primary-button vault-next-vault-button" type="button" data-vault-roll>${escapeHtml(
                        t("vaultNextVault")
                    )}</button>
                </div>
            `;
        }

        if (isMassiveLootRevealActive(vault, now)) {
            const pointMultiplier = normalizePointMultiplier(vault.pointMultiplier);

            return `
                <div class="vault-control-row">
                    <button class="primary-button" type="button" disabled>${escapeHtml(
                        `${t("vaultMassiveLootLabel")} x${pointMultiplier}!`
                    )}</button>
                </div>
            `;
        }

        const crackLabel =
            vault.crackNumber === null
                ? t("vaultCrack", { number: "" }).trim()
                : t("vaultCrack", {
                      number: String(vault.crackStartedAt === null ? vault.crackNumber + 1 : vault.crackNumber)
                  });
        if (vault.crackStartedAt !== null && now < vault.crackStartedAt) {
            return `
                <div class="vault-control-row">
                    <button class="primary-button vault-reveal-button" type="button" data-vault-finish-round>${escapeHtml(t("reveal"))}</button>
                </div>
            `;
        }

        if (vault.crackStartedAt !== null) {
            return `
                <div class="vault-control-row">
                    <button class="primary-button vault-reveal-button" type="button" data-vault-finish-round>${escapeHtml(t("reveal"))}</button>
                </div>
            `;
        }

        return `
            <div class="vault-control-row">
                <button class="primary-button" type="button" data-vault-next-crack>${escapeHtml(crackLabel)}</button>
                <button class="secondary-button" type="button" data-vault-finish-round>${escapeHtml(t("reveal"))}</button>
            </div>
        `;
    }

    private renderTeamList(vault: VaultBreakerRoomView, now: number): string {
        if (vault.teams.length === 0) {
            return `<p class="roster-empty vault-team-empty">${escapeHtml(t("noStudentsYet"))}</p>`;
        }

        return `
            <ul class="${getPlayerRosterListClass("roster-list vault-team-list", vault.teams.length)}">
                ${vault.teams.map((team) => this.renderTeamRow(team, vault, now)).join("")}
            </ul>
        `;
    }

    private renderTeamRow(team: VaultBreakerTeamView, vault: VaultBreakerRoomView, now: number): string {
        const awayClass = team.isAway ? " is-away" : "";
        const leftClass = hasTeamLeft(team) ? " is-left" : "";
        const solvedClass = team.solved ? " is-solved" : "";
        const displayName = getPlayerRosterDisplayName(team.name, team.renamePending, t("renamePendingName"));
        const isSetupChooser = vault.currentVault === null && team.sessionId === vault.chooserSessionId;
        const chooserClass = isSetupChooser ? " is-chooser" : "";
        const status = getTeamStatus(team, vault, now);
        const showStatusLed = status !== null && (vault.currentVault !== null || team.isAway);
        const statusClass = showStatusLed && status ? ` is-status-${status.tone}` : "";
        const scorePopup = this.renderTeamScorePopupMarkup(team.sessionId);
        const placementClass = getPlayerRosterPlacementClass(team.placement);
        const statusLed = showStatusLed && status
            ? `<span class="vault-team-status-led is-${escapeHtml(status.tone)}" title="${escapeHtml(
                  status.label
              )}" aria-label="${escapeHtml(status.label)}"><span class="visually-hidden">${escapeHtml(status.label)}</span></span>`
            : "";
        const lockPositionBadge = status?.tone === "armed" ? renderVaultTeamLockPositionBadge(team) : "";
        const awayLabel = hasTeamLeft(team) ? t("vaultTeamLeft") : t("away");
        const awayBadge = team.isAway
            ? `<span class="vault-team-away-badge" data-away-reason="${escapeHtml(
                  team.awayReason ?? "away"
              )}" title="${escapeHtml(awayLabel)}" aria-label="${escapeHtml(awayLabel)}">${escapeHtml(awayLabel)}</span>`
            : "";
        const actionsKey = `vault:${team.sessionId}`;
        const actionMenu = renderPlayerRosterActionsMenu(
            `${t("playerActions")}: ${displayName}`,
            [
                {
                    attributes: {
                        "data-vault-score-session": team.sessionId,
                        "data-vault-score-delta": "100",
                        "aria-label": `${t("addPoint")}: ${displayName}`,
                        title: `${t("addPoint")}: ${displayName}`
                    },
                    className: "vault-team-score-plus",
                    label: `+${formatMoney(100)}`
                },
                {
                    attributes: {
                        "data-vault-score-session": team.sessionId,
                        "data-vault-score-delta": "-100",
                        "aria-label": `${t("subtractPoint")}: ${displayName}`,
                        title: `${t("subtractPoint")}: ${displayName}`
                    },
                    className: "vault-team-score-minus",
                    label: `-${formatMoney(100)}`
                },
                {
                    attributes: {
                        "data-vault-prompt-rename": team.sessionId,
                        "aria-label": `${t("askRename")}: ${displayName}`,
                        title: `${t("askRename")}: ${displayName}`
                    },
                    className: "vault-team-rename-prompt-button",
                    label: t("askRename")
                },
                {
                    attributes: {
                        "data-vault-kick": team.sessionId,
                        "aria-label": `${t("kick")}: ${displayName}`,
                        title: `${t("kick")}: ${displayName}`
                    },
                    className: "vault-team-kick-button",
                    label: t("kick")
                }
            ],
            {
                align: "start",
                key: actionsKey,
                open: this.openRosterActionsKey === actionsKey,
                preferredPlacement: "below",
                scope: "vault",
                triggerClassName: `vault-team-name-trigger ${getVaultPlayerNameFitClass(displayName)}`,
                triggerLabel: displayName
            }
        );

        return `
            <li data-vault-team-session="${escapeHtml(team.sessionId)}" class="roster-row vault-team-row${awayClass}${leftClass}${solvedClass}${chooserClass}${statusClass}${placementClass}${getPlayerRenamePendingClass(
                team.renamePending
            )}">
                <div class="vault-team-identity">
                    <div class="vault-team-heading">
                        <span class="vault-team-name-line">
                            ${actionMenu}
                        </span>
                        <span class="vault-team-indicators">
                            ${lockPositionBadge}
                            ${awayBadge}
                            ${statusLed}
                        </span>
                    </div>
                    <span class="vault-team-score-line">
                        <span class="vault-team-score-wrap">
                            <span class="vault-team-score">${escapeHtml(formatMoney(team.score))}</span>
                            ${scorePopup}
                        </span>
                        ${
                            vault.currentVault === null && team.sessionId === vault.chooserSessionId
                                ? `<span class="vault-chooser-badge" title="${escapeHtml(
                                      t("vaultChooser")
                                  )}" aria-label="${escapeHtml(t("vaultChooser"))}"><span class="visually-hidden">${escapeHtml(
                                      t("vaultChooser")
                                  )}</span></span>`
                                : ""
                        }
                        <span class="vault-team-meta">
                            ${renderTeamCrackMeter(team, vault)}
                        </span>
                    </span>
                </div>
            </li>
        `;
    }

    private renderAwardOverlay(now: number): string {
        const activeAward = this.activeAwardAnimation;

        if (!activeAward) {
            return "";
        }

        const elapsedMs = Math.max(0, now - activeAward.startedAt);

        if (elapsedMs > 2600) {
            return "";
        }

        const award = activeAward.award;
        const parts = getVaultAwardBreakdownItems(award);
        const rankLabel =
            shouldShowStandaloneRank(award) && award.lockRank !== null ? formatLockPositionLabel(award.lockRank, award.lockCount) : "";
        const toneClass = award.correct ? " is-correct" : " is-effort";

        return `
            <div class="vault-award-pop${toneClass}" style="--vault-award-delay: -${Math.round(elapsedMs)}ms;" aria-hidden="true">
                <div class="vault-award-card">
                    <strong>${escapeHtml(formatMoneyDelta(award.scoreDelta))}</strong>
                    <span>${escapeHtml(t(award.correct ? "vaultAwardCracked" : "vaultAwardLogged"))}</span>
                    ${
                        parts.length
                            ? `<div class="${getVaultAwardBreakdownClass(
                                  "vault-award-parts",
                                  parts
                              )}" aria-hidden="true">${renderVaultAwardBreakdownBadges(parts)}</div>`
                            : ""
                    }
                    ${rankLabel ? `<em>${escapeHtml(rankLabel)}</em>` : ""}
                </div>
            </div>
        `;
    }

    private renderPlayerFeedback(room: RoomView, vault: VaultBreakerRoomView, now: number): string {
        const viewer = vault.viewer;
        const award = viewer?.lastAward ?? null;
        const feedbackClass = viewer?.lastFeedbackKind ? ` is-${viewer.lastFeedbackKind}` : "";
        const selectedChoice = viewer?.choices.find((choice) => choice.id === viewer.selectedChoiceId) ?? null;
        const selectedRankLabel = formatSelectedChoiceLockRank(selectedChoice);
        const selectedChoiceInfo = getVaultBreakerSupplementalInfo(selectedChoice?.korean);

        if (award && award.correct && award.scoreDelta > 0) {
            const parts = getVaultAwardBreakdownItems(award);
            const rankLabel =
                shouldShowStandaloneRank(award) && award.lockRank !== null ? formatLockPositionLabel(award.lockRank, award.lockCount) : "";

            return `
                <div class="vault-feedback${feedbackClass} has-award-breakdown" role="status" aria-live="polite">
                    <span class="vault-feedback-main">
                        <span>${escapeHtml(t(award.correct ? "vaultAwardCracked" : "vaultAwardLogged"))}</span>
                        <strong>${escapeHtml(formatMoneyDelta(award.scoreDelta))}</strong>
                    </span>
                    ${
                        parts.length
                            ? `<span class="${getVaultAwardBreakdownClass(
                                  "vault-feedback-badges",
                                  parts
                              )}" aria-hidden="true">${renderVaultAwardBreakdownBadges(parts)}</span>`
                            : ""
                    }
                    ${rankLabel ? `<span class="vault-feedback-rank">${escapeHtml(rankLabel)}</span>` : ""}
                </div>
            `;
        }

        const playerStatus = getVaultPlayerStatus(vault, now);

        if (playerStatus?.tone === "wrong") {
            return `
                <div class="vault-feedback is-status is-${escapeHtml(playerStatus.tone)}" role="status" aria-live="polite">
                    ${escapeHtml(playerStatus.label)}
                </div>
            `;
        }

        if (selectedChoice && selectedRankLabel && viewer?.lastFeedbackKind === "info") {
            return `
                <div class="vault-feedback${feedbackClass} has-selected-code" role="status" aria-live="polite">
                    <span class="vault-selected-code-main">
                        <span class="vault-selected-code-word">${escapeHtml(formatVaultBreakerDisplayTerm(selectedChoice.answer))}</span>
                        <strong>${escapeHtml(selectedRankLabel)}</strong>
                    </span>
                    ${selectedChoiceInfo ? `<span class="vault-selected-code-korean">${escapeHtml(selectedChoiceInfo)}</span>` : ""}
                </div>
            `;
        }

        if (playerStatus) {
            return `
                <div class="vault-feedback is-status is-${escapeHtml(playerStatus.tone)}" role="status" aria-live="polite">
                    ${escapeHtml(playerStatus.label)}
                </div>
            `;
        }

        const rawFeedback = viewer?.lastFeedback ?? room.statusMessage;
        if (!rawFeedback.trim() || rawFeedback === "Arm a vault code.") return "";
        const feedbackText = localizeRuntimeText(rawFeedback);

        return `
            <div class="vault-feedback${feedbackClass}" role="status" aria-live="polite">
                ${escapeHtml(feedbackText)}
            </div>
        `;
    }

    private renderPlayer(room: RoomView, vault: VaultBreakerRoomView, now: number): string {
        const viewer = vault.viewer;
        const current = vault.currentVault;
        const timer = getVaultTimerDisplay(vault, now);
        const growingStashRevealActive = isGrowingStashRevealActive(vault, now);
        const massiveLootRevealActive = isMassiveLootRevealActive(vault, now);
        const specialRevealActive = growingStashRevealActive || massiveLootRevealActive;
        const choices = viewer?.choices ?? [];
        const feedback = growingStashRevealActive
            ? renderVaultGrowingStashReveal(current?.growingStashBonusPoints ?? vault.growingStashBonusPoints)
            : massiveLootRevealActive
              ? renderVaultMassiveLootReveal(current?.pointMultiplier ?? vault.pointMultiplier)
              : this.renderPlayerFeedback(room, vault, now);
        const crackLabel = vault.crackNumber !== null ? `C${vault.crackNumber}` : "";
        const timerCrackClass = vault.crackNumber !== null ? ` is-crack-${vault.crackNumber}` : "";
        const surfaceMotionClass = this.getSurfaceMotionClass();

        return `
            <section class="vault-shell vault-player-shell">
                ${current ? this.renderCategoryStrip(vault) : ""}
                <header class="vault-player-head">
                    <div class="vault-player-score">
                        <span>${escapeHtml(t("vaultPlayerTotal"))}</span>
                        <strong>${escapeHtml(formatMoney(viewer?.score ?? 0))}</strong>
                    </div>
                    <div class="vault-player-meter-stack">
                        <div class="vault-player-vault">
                            <span>${escapeHtml(t("worth"))}</span>
                            <strong${current ? ` aria-label="${escapeHtml(formatVaultDisplayValue(current))}"` : ""}>${
                                current ? renderVaultDisplayValue(current, { compact: true }) : "--"
                            }</strong>
                            ${current ? renderVaultMassiveLootBadge(current.pointMultiplier) : ""}
                        </div>
                        <div class="vault-player-timer${timerCrackClass}">
                            ${crackLabel ? `<span class="vault-player-crack-label">${escapeHtml(crackLabel)}</span>` : ""}
                            ${renderVaultTimerRing(timer, "is-player", this.getCrackHandoffKey(vault, now))}
                        </div>
                    </div>
                </header>
                <main class="vault-player-main">
                    <section data-vault-surface-key="${escapeHtml(this.surfaceMotionKey ?? "")}" class="vault-choice-card${feedback ? "" : " has-no-feedback"}${surfaceMotionClass}">
                        <span class="vault-translation-bar" aria-hidden="true"></span>
                        ${feedback ? `<div class="vault-scanner-panel">${feedback}</div>` : ""}
                        ${
                            specialRevealActive
                                ? ""
                                : this.renderChoices(
                                      choices,
                                      viewer?.selectedChoiceId ?? null,
                                      Boolean(viewer?.canSelect),
                                      current === null
                                  )
                        }
                    </section>
                </main>
                ${this.renderAwardOverlay(now)}
            </section>
        `;
    }

    private renderChoices(
        choices: readonly VaultBreakerChoiceView[],
        selectedChoiceId: string | null,
        canSelect: boolean,
        showWaitingCapy: boolean
    ): string {
        if (choices.length === 0) {
            return showWaitingCapy
                ? `<div class="vault-choice-empty vault-player-waiting-stage" aria-hidden="true"><span class="vault-player-waiting-capy-slot" data-vault-player-waiting-capy></span></div>`
                : `<div class="vault-choice-empty" aria-hidden="true"></div>`;
        }

        const densityClass = choices.length <= 6 ? " is-focused" : choices.length <= 12 ? " is-medium" : " is-compact";

        return `
            <div class="vault-choice-grid${densityClass}" style="--vault-choice-count: ${choices.length};">
                ${choices
                    .map((choice) => {
                        const isSelected = choice.id === selectedChoiceId;
                        const isChoiceSelectable = choice.selectable !== false;
                        const supplementalInfo = getVaultBreakerSupplementalInfo(choice.korean);
                        const classes = ["vault-choice-button", isSelected ? "is-selected" : "", isChoiceSelectable ? "" : "is-inactive"]
                            .filter(Boolean)
                            .join(" ");

                        return `
                            <button
                                class="${classes}"
                                type="button"
                                data-vault-choice-id="${escapeHtml(choice.id)}"
                                aria-pressed="${isSelected ? "true" : "false"}"
                                ${canSelect && isChoiceSelectable ? "" : "disabled"}
                            >
                                <span class="vault-choice-answer">${escapeHtml(formatVaultBreakerDisplayTerm(choice.answer))}</span>
                                ${supplementalInfo ? `<span class="vault-choice-korean">${escapeHtml(supplementalInfo)}</span>` : ""}
                            </button>
                        `;
                    })
                    .join("")}
            </div>
        `;
    }
}

export const createVaultBreakersView = (root: HTMLElement, options: VaultBreakersViewOptions): VaultBreakersView =>
    new VaultBreakersViewController(root, options);
